// Highmarch VFX — sprite particles, lightning, beams, projectiles, ground decals and light slots.
// Depends on three (module). One atlas texture + frame JSON from vfx_atlas.py.
// Usage: const vfx = new VFX(scene, camera, atlasTex, atlasMeta); vfx.cannonFire(a, b); ... vfx.update(dt)
//
// Draw calls: two instanced sprite layers (additive, normal), one instanced ground-decal layer, and one mesh per
// live bolt/beam (pooled). Nothing here allocates a THREE object per frame; a spawn reuses a dead particle.
//
// Phase-1 "make it pop" (claude/ART_WOW_PASS.md, art_review/vfx.md): colour language (one PALETTE), camera trauma
// (`kick` → `takeTrauma`, the client's IsoCamera applies it), budget classes (`PRIO`, `lod`, `cap`), pooled 3-layer
// bolts on one shared path, VFX light slots (`light` → render/vfxLights.ts) and ground-conforming decals (`decal`).
import * as THREE from 'three';

const MAX = 4000;

// ---------------------------------------------------------------- colour language (vfx.md F3)
// One table so an element reads the same on every effect. Linear RGB. Frost owns cyan; lightning and the fortress
// strike are violet-white; the cannon is a dirt hit (kinetic), fire keeps orange; poison is green; holy is gold.
export const PALETTE = {
  fire:    { core: [1, 0.85, 0.5], rim: [1, 0.42, 0.1], smoke: [0.28, 0.24, 0.23] },
  frost:   { core: [1, 1, 1], rim: [0.55, 0.88, 1], mote: [0.8, 0.95, 1] },
  poison:  { core: [0.8, 1, 0.5], rim: [0.35, 0.85, 0.2], smoke: [0.42, 0.66, 0.3] },
  arcane:  { core: [1, 1, 1], rim: [0.72, 0.55, 1], hex: 0xb88cff, coreHex: 0xf4eeff },   // lightning + fortress
  kinetic: { core: [1, 0.95, 0.85], dust: [0.55, 0.48, 0.4], smoke: [0.32, 0.3, 0.29], clod: [0.42, 0.33, 0.24] },
  holy:    { core: [1, 0.97, 0.8], rim: [1, 0.8, 0.3], hex: 0xffe08a },
  blood:   { core: [1, 0.75, 0.6], rim: [1, 0.32, 0.18] },                                // boss arrival
};

// ---------------------------------------------------------------- budget (vfx.md F12)
// Priority classes: when the pool is 85 % full trail and ambient spawns are dropped; at the cap only hero effects
// (the fortress strike, boss arrival/fall) may spawn. `lod` scales every burst's count by the quality tier.
export const PRIO = { ambient: 0, trail: 1, muzzle: 2, impact: 3, hero: 4 };
/**
 * The tier-up celebration (`towerLevelUp`). World units and seconds: the upright shockwave; the spark fountain
 * (`fountainWaves` bursts of `fountainPerWave`, `fountainGap` apart); the halo (`rings` flat rings from the atlas
 * `shock` strip's frame `ringFrame`, `ringGap` apart, each sweeping from the foot to `ringOver` above the roof in
 * `ringLife`, shrinking `ringSize[0]` -> `[1]`).
 */
export const LEVEL_UP = {
  shockSize: 2.8, shockLife: 0.45, fountainWaves: 4, fountainPerWave: 7, fountainGap: 0.14,
  rings: 3, ringFrame: 1, ringGap: 0.12, ringLife: 0.6, ringOver: 0.2, ringSize: [2.4, 1.9], ringBright: 2.0,
};
/**
 * The element towers' shots (world units, seconds; owner report 2026-09-23, "the frost tower just seems to flash"):
 * each winds up on the tower, travels and lands at the size of what the sim did — frost, fire and poison hit ONE enemy
 * (no splash radius in towers.json), lightning chains, the beacon is an aura and never fires.
 * - frost: the crystal gathers `chargeMotes` motes from `chargeRadius` and a cold glow swelling to `chargeGlow` until
 *   the release, then gone in `chargeTail` (anticipation, not a flash); `mist` is the pale cold smoke.
 * - lightning: each chain hop lands `hopDelay` after the one before so the jump reads; `afterglow` s of thin residual
 *   arc stays on each link.
 */
export const ELEMENT = {
  frost: { chargeMotes: 10, chargeRadius: 0.42, chargeGlow: 0.42, chargeTail: 0.07, mist: [0.8, 0.9, 1] },
  lightning: { hopDelay: 0.06, afterglow: 0.3 },
};
/**
 * The fire tower's look (owner report 2026-09-24, "fire tower also needs a closer look visually": its flames, the
 * fireball and the smoke read as flat pale-orange blobs and cotton balls on a phone). World units and seconds.
 * - The hot ramp: a data frame drawn with `ramp: 'hot'` (or `vfx.hot(heat)`) maps its temperature through `hotStops`
 *   — deep red at the rim, orange, yellow, a white-yellow core — instead of the atlas `fire` row, whose whole range is
 *   pale orange to cream. The frame's temperature from `hotT0` to `hotT1` spans the ramp, times the sprite's heat
 *   (`heat` below: how hot each effect burns); below `hotT0` a texel fades out over `hotFade` of the ramp, so a flame
 *   ends in a soft red rim rather than a hard silhouette. The core is lifted up to `hotGain`× (on heat²) past the bloom
 *   threshold, so only the hottest part blooms. A sprite cools as it fades: at alpha 0 its heat is `hotCool` of full,
 *   so a dying flame reddens and thins instead of going translucent cream.
 * - The fireball (`fireball`, re-stamped each frame in place of the GLB's flat emissive shroud): a white-hot `core`, a
 *   flame `body` trailing behind the flight, `bodyLength` × its width, and a red `halo` of heat, at the round's scale.
 * - The tower's braziers (`towerFlame`): each flame part is drawn as a flame tongue `tongue`× the height of the cone it
 *   replaces, flickering at `flicker` flipbook frames/s, over a warm `bowl` glow.
 * - Smoke off fire is soot, not steam: `soot` at `sootAlpha`, easing in over `sootIn` and thinning out over `sootOut`
 *   of its life, growing `sootGrow`× as it rises.
 */
export const FIRE = {
  hotT0: 0.34, hotT1: 1, hotFade: 0.22, hotGain: 1.3, hotCool: 0.5,
  hotStops: [[0, [0.4, 0.03, 0.0]], [0.3, [0.95, 0.2, 0.02]], [0.55, [1.0, 0.48, 0.07]], [0.78, [1.0, 0.75, 0.3]], [0.95, [1.0, 0.95, 0.75]]],
  heat: { brazier: 0.8, body: 1, wisp: 0.7, gout: 0.95, burst: 0.8, lick: 0.9, burn: 0.8 },
  core: 0.2, coreColor: [1, 0.82, 0.5], coreBright: 1.5,
  body: 0.38, bodyLength: 2.4, halo: 1, haloColor: [1, 0.25, 0.03], haloAlpha: 0.7,
  tongue: 1.3, flicker: 16, bowl: 1.1, bowlColor: [1, 0.38, 0.06], bowlAlpha: 0.45,
  soot: [0.13, 0.11, 0.1], sootAlpha: 0.42, sootIn: 0.2, sootOut: 0.7, sootGrow: 3.2,
};
/**
 * `iRamp` of the procedural hot ramp, past the atlas's ramp rows so the shader tells it apart: HOT_RAMP + heat (0–1],
 * so one float carries both the ramp and how hot the sprite burns.
 */
const HOT_RAMP = 100;
const glslF = (x) => (Number.isInteger(x) ? x.toFixed(1) : String(x));
const glslV3 = (c) => `vec3(${c.map(glslF).join(', ')})`;
/** FIRE.hotStops as GLSL: heat 0–1 → linear colour, lifted toward the core. */
const HOT_RAMP_GLSL = (() => {
  const S = FIRE.hotStops;
  let body = `vec3 c = ${glslV3(S[0][1])};\n`;
  for (let i = 1; i < S.length; i++) body += `  c = mix(c, ${glslV3(S[i][1])}, smoothstep(${glslF(S[i - 1][0])}, ${glslF(S[i][0])}, h));\n`;
  return `vec3 hotRamp(float h) {\n  ${body}  return c * (1.0 + ${glslF(FIRE.hotGain)} * h * h);\n}`;
})();
/**
 * The fortress's shot between two strikes (`baseZap`). The sim fires the fortress every 13 ticks (0.65 s at ×1);
 * the full strike (charge, sky bolt, pillar, hit-stop, screen flash) is its hero beat every couple of seconds, and the
 * shots in between are one clear bolt from the conductor to the target: `life` s, mid ribbon `width` with `glow` /
 * `core` (units, ×tier scale) at `coreBright`, and a `tipFlash`-sized flash on the conductor.
 */
export const BASE_ZAP = { life: 0.2, width: 0.14, glow: 0.42, core: 0.045, coreBright: 2.6, tipFlash: 0.6 };
/**
 * Status marks on afflicted enemies (`enemyStatus`), in fractions of the unit's height `h` unless noted:
 * - frost: `crystals` ice crystals (`frozenCrystals` when held still) stand out of the body at `crystalRing`·h from its
 *   axis, between `crystalLow` and `crystalHigh`, each `crystalSize`·h; a milky ice glaze `glaze`·h over the body at
 *   `glazeAlpha` (`frozenGlazeAlpha`); a cold disc `frostDisc`·h under the feet at `frostDiscAlpha`; a glint every
 *   1/`frostMoteRate` s.
 * - burn: one flame tongue per stack (the sim stacks one per Fire tower, up to 3), `flameSize`·h, an ember every
 *   1/`emberRate` s per stack.
 * - shred (poison): one green pip per stack orbiting at `pipRing`·h, `pipHeight`·h up, `pipSpin` rad/s; bubbles.
 * `maxStamps`: the most marks drawn in one frame (they share the particle layers; no extra draw).
 */
export const STATUS = {
  maxStamps: 400,
  crystals: 4, frozenCrystals: 6, crystalRing: 0.2, crystalLow: 0.1, crystalHigh: 0.6, crystalSize: 0.75, frostDisc: 1.8, frostDiscAlpha: 0.4, frostMoteRate: 3,
  glaze: 1.25, glazeAlpha: 0.22, frozenGlazeAlpha: 0.45,
  flameSize: 0.85, flameRing: 0.22, emberRate: 2.5, burnBright: 0.7,
  pipRing: 0.55, pipHeight: 0.6, pipSize: 0.36, pipSpin: 2.4, bubbleRate: 1.5,
};
const ICE = ['ice_a', 'ice_b', 'ice_c'];
const LOD_BY_TIER = [1, 0.8, 0.6, 0.4, 0.25];            // ultra, high, medium, low, lowest (render/quality.ts order)
const CAP_BY_TIER = [1600, 1200, 900, 600, 400];
/** What a refused spawn returns, so a caller that sets `.flat` on the result doesn't need to check. */
const DROPPED = { pos: new THREE.Vector3(), vel: new THREE.Vector3(), flat: false };

/**
 * The basis a velocity-aligned sprite is drawn in (VERT's aligned branch, mirrored here so it can be tested without
 * GL): x along the axis as it lies on the camera plane, y perpendicular to it on screen; null when the axis points at
 * the camera (no screen direction: the sprite stays camera-facing). camRight and camUp are unit and orthogonal.
 * Arrays are [x, y, z].
 */
export function alignedBasis(axis, camRight, camUp) {
  const cr = (u, v) => [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]];
  const dot = (u, v) => u[0] * v[0] + u[1] * v[1] + u[2] * v[2];
  const n = cr(camRight, camUp), k = dot(axis, n);
  const a = [axis[0] - k * n[0], axis[1] - k * n[1], axis[2] - k * n[2]];
  const aa = dot(a, a);
  if (!(aa > 1e-8 * dot(axis, axis))) return null;
  const l = Math.sqrt(aa), right = [a[0] / l, a[1] / l, a[2] / l];
  return { right, up: cr(n, right) };
}

const VERT = /* glsl */`
attribute vec3 iPos; attribute vec2 iSize; attribute float iRot; attribute vec4 iColor; attribute vec4 iUV; attribute vec3 iAxis; attribute float iRamp;
varying vec2 vUv; varying vec4 vColor; varying vec2 vQ; varying float vRamp;
uniform vec3 camRight; uniform vec3 camUp;
void main(){
  vUv = iUV.xy + uv * iUV.zw;
  vQ = uv;
  vColor = iColor;
  vRamp = iRamp;
  vec2 p = (position.xy) * iSize;            // quad is [-0.5,0.5]
  float c = cos(iRot), s = sin(iRot);
  vec2 q = vec2(p.x*c - p.y*s, p.x*s + p.y*c);
  vec3 right = camRight, up = camUp;
  if (iAxis.y > 1.5 && iAxis.x == 0.0 && iAxis.z == 0.0) {
    right = vec3(1.0, 0.0, 0.0); up = vec3(0.0, 0.0, -1.0);
  } else if (iAxis.x != 0.0 || iAxis.y != 0.0 || iAxis.z != 0.0) {
    // velocity-aligned: x along the axis as it lies on screen, y perpendicular to it on screen. The axis is projected
    // onto the camera plane first: the old form, up = normalize(cross(cross(axis, camUp), axis)), normalised a zero
    // vector whenever the axis ran along the camera's up (a spark or flame thrown straight up or down the screen),
    // which is NaN on GPUs, so the quad vanished. An axis pointing at the camera has no screen direction and stays
    // camera-facing.
    vec3 n = cross(camRight, camUp);
    vec3 a = iAxis - dot(iAxis, n) * n;
    if (dot(a, a) > 1e-8 * dot(iAxis, iAxis)) {
      right = normalize(a);
      up = cross(n, right);
    }
  }
  vec3 wp = iPos + right * q.x + up * q.y;
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}`;
// Atlas v2 (vfx_atlas.py): `iRamp` >= 0 marks a DATA frame (R density, G top-lit shading, B temperature, linear after
// the sRGB decode) drawn through ramp row iRamp — a per-element colour ramp (fire, dirt, frost, poison, arcane, holy,
// smoke, blood) read from the same atlas at LOD 0. The ramp's alpha is its emissive share: hot texels glow at
// 1 + 0.8·T and ignore the shading, cool ones (smoke, dust) take the top-left key. iRamp = -1 is the v1 colour path;
// iRamp = -2 is a colour frame drawn WITHOUT the round mask (the digit glyphs, which fill their cell).
const FRAG = /* glsl */`
uniform sampler2D map; uniform vec4 uRamp; varying vec2 vUv; varying vec4 vColor; varying vec2 vQ; varying float vRamp;
${HOT_RAMP_GLSL}
void main(){
  vec4 t = texture2D(map, vUv);
  // A round mask over every sprite quad. Some frames touch their cell's edge (the last 'shock' ring frame has alpha
  // ~96 on its borders), and a large upright ring then drew as a pale translucent SQUARE — the phase-1 capture's
  // square around the fortress conductor during the charge. Corners of a quad are never wanted.
  float edge = vRamp < -1.5 ? 1.0 : 1.0 - smoothstep(0.44, 0.5, length(vQ - 0.5));
  vec3 rgb = t.rgb;
  float alpha = t.a * vColor.a * edge;
  if (vRamp > ${glslF(HOT_RAMP - 0.5)}) {
    // the procedural fire ramp (FIRE): heat from the temperature channel at the sprite's heat, cooling as it fades
    float heat = clamp((t.b - ${glslF(FIRE.hotT0)}) / ${glslF(FIRE.hotT1 - FIRE.hotT0)}, 0.0, 1.0) * clamp(vRamp - ${glslF(HOT_RAMP)}, 0.0, 1.0) * mix(${glslF(FIRE.hotCool)}, 1.0, vColor.a);
    rgb = hotRamp(heat);
    alpha *= smoothstep(0.0, ${glslF(FIRE.hotFade)}, heat);
  } else if (vRamp > -0.5) {
    float T = clamp(t.b, 0.0, 1.0);
    vec4 rc = textureLod(map, vec2(uRamp.x + T * uRamp.z, uRamp.y + floor(vRamp + 0.5) * uRamp.w), 0.0);
    float lit = mix(0.28 + t.g, 1.0 + 0.8 * T, rc.a);
    rgb = rc.rgb * lit;
  }
  gl_FragColor = vec4(rgb * vColor.rgb, alpha);
  #include <colorspace_fragment>
}`;

class Layer {
  constructor(scene, tex, additive, rampUv = new THREE.Vector4()) {
    const g = new THREE.InstancedBufferGeometry();
    const quad = new THREE.PlaneGeometry(1, 1);
    g.index = quad.index; g.attributes.position = quad.attributes.position; g.attributes.uv = quad.attributes.uv;
    this.pos = new Float32Array(MAX * 3); this.size = new Float32Array(MAX * 2); this.rot = new Float32Array(MAX);
    this.col = new Float32Array(MAX * 4); this.uvr = new Float32Array(MAX * 4); this.axis = new Float32Array(MAX * 3); this.ramp = new Float32Array(MAX).fill(-1);
    const mk = (a, n) => { const b = new THREE.InstancedBufferAttribute(a, n); b.setUsage(THREE.DynamicDrawUsage); return b; };
    g.setAttribute('iPos', mk(this.pos, 3)); g.setAttribute('iSize', mk(this.size, 2)); g.setAttribute('iRot', mk(this.rot, 1));
    g.setAttribute('iColor', mk(this.col, 4)); g.setAttribute('iUV', mk(this.uvr, 4)); g.setAttribute('iAxis', mk(this.axis, 3)); g.setAttribute('iRamp', mk(this.ramp, 1));
    this.geo = g;
    this.keys = ['iPos', 'iSize', 'iRot', 'iColor', 'iUV', 'iAxis', 'iRamp'];
    this.mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      uniforms: { map: { value: tex }, uRamp: { value: rampUv }, camRight: { value: new THREE.Vector3(1, 0, 0) }, camUp: { value: new THREE.Vector3(0, 1, 0) } },
    });
    this.mesh = new THREE.Mesh(g, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = additive ? 20 : 10;
    scene.add(this.mesh);
    this.n = 0;
  }
  begin() { this.n = 0; }
  push(p, x, y, z, w, h, rot, r, g, b, a, uv, ax, ay, az, ramp = -1) {
    if (this.n >= MAX) return;
    const i = this.n++;
    this.pos[i * 3] = x; this.pos[i * 3 + 1] = y; this.pos[i * 3 + 2] = z;
    this.size[i * 2] = w; this.size[i * 2 + 1] = h; this.rot[i] = rot;
    this.col[i * 4] = r; this.col[i * 4 + 1] = g; this.col[i * 4 + 2] = b; this.col[i * 4 + 3] = a;
    this.uvr[i * 4] = uv[0]; this.uvr[i * 4 + 1] = uv[1]; this.uvr[i * 4 + 2] = uv[2]; this.uvr[i * 4 + 3] = uv[3];
    this.axis[i * 3] = ax; this.axis[i * 3 + 1] = ay; this.axis[i * 3 + 2] = az;
    this.ramp[i] = ramp;
  }
  end(cam) {
    const g = this.geo;
    for (let k = 0; k < this.keys.length; k++) g.attributes[this.keys[k]].needsUpdate = true;
    g.instanceCount = this.n;
    const m = cam.matrixWorld.elements;
    this.mat.uniforms.camRight.value.set(m[0], m[1], m[2]);
    this.mat.uniforms.camUp.value.set(m[4], m[5], m[6]);
  }
}

// ---------------------------------------------------------------- ground decals (vfx.md F5.2)
// One instanced draw of 4×4-segment quads lying on the terrain: each vertex reads the level's height field from a
// small float texture (bilinear by hand with texelFetch — iOS has no linear filtering on float textures), so a ring
// or a scorch follows a slope instead of cutting into it or floating over a dip. The shapes are procedural (the v1
// atlas has no decal art): 0 ring, 1 scorch, 2 frost star, 3 poison puddle, 4 crack, 5 dust ring. Premultiplied
// blending lets one draw both darken (scorch, crack) and add light (ring, embers). transparent + depthWrite:false,
// so PostFx.keepVfxOutOfAo keeps it out of the GTAO prepass.
const DECALS = 24;
export const DECAL_TYPE = { ring: 0, scorch: 1, frost: 2, poison: 3, crack: 4, dust: 5, rune: 6, target: 7 };
const DECAL_VERT = /* glsl */`
attribute vec4 iPosSize; attribute float iRot; attribute vec4 iCol; attribute vec4 iDat;
uniform sampler2D uH; uniform vec4 uHBox; uniform vec2 uHRes; uniform float uHOn;
varying vec2 vUv; varying vec4 vCol; varying vec4 vDat;
float heightAt(vec2 xz){
  vec2 g = clamp((xz - uHBox.xy) * uHBox.zw, vec2(0.0), uHRes - 1.001);
  ivec2 i = ivec2(floor(g)); vec2 f = fract(g);
  float h00 = texelFetch(uH, i, 0).r, h10 = texelFetch(uH, i + ivec2(1, 0), 0).r;
  float h01 = texelFetch(uH, i + ivec2(0, 1), 0).r, h11 = texelFetch(uH, i + ivec2(1, 1), 0).r;
  return mix(mix(h00, h10, f.x), mix(h01, h11, f.x), f.y);
}
void main(){
  vUv = uv; vCol = iCol; vDat = iDat;
  // the plane's y runs along world −z, as a flat sprite's does: laid along +z its triangles faced DOWN, and the
  // material's default front-face culling dropped every decal — no scorch, frost star, puddle or flat ring ever drew
  vec2 l = vec2(position.x, -position.y) * iPosSize.w;
  float c = cos(iRot), s = sin(iRot);
  vec2 xz = iPosSize.xz + vec2(l.x * c - l.y * s, l.x * s + l.y * c);
  float y = (uHOn > 0.5 ? heightAt(xz) : iPosSize.y) + 0.025;
  gl_Position = projectionMatrix * viewMatrix * vec4(xz.x, y, xz.y, 1.0);
}`;
// Tuned once the decals first drew (they had never rendered before the face-up fix): a frost star at 0.6 cover of
// near-white and a poison pool at 0.62 of pale lime read as flat stickers on the grass, and the cracks' glow at 2×
// burned the boss's red and the fortress's violet to salmon and white, glowing over 40 % of a 4.5-5.5 s life (a pale
// network lying over the grass for two seconds): now a short flash of heat over the first quarter, then dark fissures.
const DECAL_TUNING = /* glsl */`
#define FROST_COVER 0.45
#define FROST_TINT 0.4
#define POISON_COVER 0.45
#define CRACK_GLOW 1.1
#define CRACK_GLOW_FADE 4.0
`;
const DECAL_FRAG = DECAL_TUNING + /* glsl */`
varying vec2 vUv; varying vec4 vCol; varying vec4 vDat;
uniform sampler2D uMap; uniform vec4 uDUV[4]; uniform float uDOn;
float dh(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float dn(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(dh(i), dh(i + vec2(1.0, 0.0)), f.x), mix(dh(i + vec2(0.0, 1.0)), dh(i + vec2(1.0, 1.0)), f.x), f.y); }
void main(){
  vec2 p = vUv * 2.0 - 1.0; float r = length(p); float a = atan(p.y, p.x);
  int type = int(vDat.x + 0.5); float t = vDat.y; float A = vDat.z; float B = vDat.w;
  vec3 base = vec3(0.0); vec3 emit = vec3(0.0); float cover = 0.0;
  if (uDOn > 0.5 && type >= 1 && type <= 4) {       // atlas v2 painted decals
    vec4 D = uDUV[type - 1]; vec4 tx = texture2D(uMap, D.xy + vUv * D.zw);
    if (type == 1) {                                 // scorch: charred blotch, ash mottling, ember cracks cooling
      cover = tx.a * 0.85; base = vCol.rgb * (0.55 + 1.1 * tx.g);
      emit = vec3(1.0, 0.45, 0.12) * B * tx.b * 1.6 * max(0.0, 1.0 - t * 3.0);
    } else if (type == 2) {                          // frost star with dendrites and glints: a rime, not a white sticker
      cover = tx.a * FROST_COVER; base = mix(vec3(0.9, 0.96, 1.0), vCol.rgb, FROST_TINT) * (0.75 + 0.25 * tx.g); emit = vCol.rgb * tx.b * B * 0.6 * (1.0 - t);
    } else if (type == 3) {                          // poison pool: lit rim, bubbles, slow ripples
      float rip = 0.5 + 0.5 * sin(r * 18.0 - t * 30.0);
      cover = tx.a * POISON_COVER; base = vCol.rgb * (0.15 + 0.4 * tx.g);
      emit = vCol.rgb * tx.a * (0.18 * tx.b + 0.2 * rip * smoothstep(0.2, 0.8, r)) * B;
    } else {                                         // crack: fissures with light lips, glowing then dark
      cover = tx.a * 0.85; base = vec3(0.05, 0.04, 0.05) + vec3(0.3, 0.27, 0.24) * tx.g;
      emit = vCol.rgb * tx.b * B * max(0.0, 1.0 - t * CRACK_GLOW_FADE) * CRACK_GLOW;
    }
  } else if (type == 0) {                                   // expanding shock ring with a soft inner flash
    float R = mix(0.25, 0.92, 1.0 - (1.0 - t) * (1.0 - t)); float w = mix(0.12, 0.04, t);
    float band = exp(-((r - R) / w) * ((r - R) / w));
    emit = vCol.rgb * B * (band + exp(-r * r * 8.0) * 0.35) * (1.0 - t);
  } else if (type == 1) {                            // scorch: dark blotch, embers cooling in the middle
    float n = dn(p * 3.0 + 13.0) * 0.5 + dn(p * 7.0) * 0.25;
    cover = smoothstep(1.0, 0.35, r + n * 0.35) * 0.8; base = vCol.rgb;
    emit = vec3(1.0, 0.45, 0.12) * B * smoothstep(0.55, 0.0, r + n * 0.3) * max(0.0, 1.0 - t * 4.0);
  } else if (type == 2) {                            // frost star: six arms and fine needles, pale white cover
    float arms = pow(abs(cos(a * 3.0)), 10.0) * smoothstep(1.0, 0.1, r) + smoothstep(0.45, 0.0, r) * 0.6;
    float fine = pow(abs(cos(a * 9.0 + r * 4.0)), 30.0) * smoothstep(0.8, 0.2, r) * 0.5;
    float m = clamp(arms + fine, 0.0, 1.0);
    cover = m * FROST_COVER; base = mix(vec3(0.9, 0.96, 1.0), vCol.rgb, FROST_TINT); emit = vCol.rgb * m * B * 0.45 * (1.0 - t);
  } else if (type == 3) {                            // poison puddle: lumpy green pool with slow ripples
    float n = dn(p * 4.0 + 7.0);
    float m = smoothstep(0.95, 0.6, r + (n - 0.5) * 0.35);
    float rip = 0.5 + 0.5 * sin(r * 18.0 - t * 30.0);
    cover = m * POISON_COVER; base = vCol.rgb * 0.3; emit = vCol.rgb * m * (0.15 + 0.22 * rip * smoothstep(0.2, 0.8, r)) * B;
  } else if (type == 4) {                            // crack: jagged radial fissures, glowing then dark
    float ang = a * 7.0 / 6.2831853;
    float f = abs(fract(ang + (dn(vec2(r * 3.0, floor(ang))) - 0.5) * 0.35) - 0.5);
    float line = smoothstep(0.06, 0.0, f * (0.4 + r)) * smoothstep(1.0, 0.15, r);
    cover = line * 0.85; base = vec3(0.05, 0.04, 0.05); emit = vCol.rgb * line * B * max(0.0, 1.0 - t * CRACK_GLOW_FADE) * CRACK_GLOW;
  } else if (type == 6) {                            // rune circle: two rings, twelve ticks, six glyph spokes; glows
    float ring1 = exp(-pow((r - 0.9) / 0.025, 2.0)), ring2 = exp(-pow((r - 0.68) / 0.018, 2.0));
    float ticks = step(0.92, abs(cos(a * 6.0))) * smoothstep(0.7, 0.72, r) * smoothstep(0.88, 0.86, r);
    float spokes = pow(abs(cos(a * 3.0)), 60.0) * smoothstep(0.2, 0.3, r) * smoothstep(0.68, 0.6, r);
    float glyph = step(0.5, dn(vec2(floor(a * 24.0 / 6.2831853) * 3.1, 1.0))) * smoothstep(0.74, 0.76, r) * smoothstep(0.84, 0.82, r);
    float m = clamp(ring1 + ring2 + ticks + spokes * 0.8 + glyph * 0.7, 0.0, 1.0);
    emit = vCol.rgb * m * B; cover = m * 0.15; base = vCol.rgb * 0.2;
  } else if (type == 7) {                            // target (the Mortar's landing point): a ring the burst's size,
    // four ticks closing in and a faint fill, tightening and brightening as t runs 0 → 1 (the client drives t)
    float ring = exp(-pow((r - 0.9) / 0.035, 2.0));
    float reach = mix(0.45, 0.82, t);
    float ticks = step(0.97, abs(cos(a * 2.0))) * smoothstep(reach - 0.02, reach, r) * step(r, 0.86);
    float dot = smoothstep(0.08, 0.0, r);
    float fill = 0.12 * smoothstep(0.95, 0.3, r);
    float m = ring + ticks * 0.9 + dot + fill;
    emit = vCol.rgb * m * B * (0.45 + 0.55 * t); cover = m * 0.2; base = vCol.rgb * 0.15;
  } else {                                           // dust ring: a brown skirt thrown outward
    float R = mix(0.2, 0.95, 1.0 - (1.0 - t) * (1.0 - t));
    cover = smoothstep(0.3, 0.0, abs(r - R)) * (0.6 + 0.4 * dn(p * 5.0)) * 0.55 * (1.0 - t); base = vCol.rgb;
  }
  float k = A * smoothstep(1.0, 0.88, r);
  gl_FragColor = vec4(base * cover * k + emit * k, cover * k);
  #include <colorspace_fragment>
}`;

class DecalLayer {
  constructor(scene, tex = null, rects = []) {
    const plane = new THREE.PlaneGeometry(1, 1, 4, 4);
    const g = new THREE.InstancedBufferGeometry();
    g.index = plane.index; g.attributes.position = plane.attributes.position; g.attributes.uv = plane.attributes.uv;
    this.ps = new Float32Array(DECALS * 4); this.rot = new Float32Array(DECALS);
    this.col = new Float32Array(DECALS * 4); this.dat = new Float32Array(DECALS * 4);
    const mk = (a, n) => { const b = new THREE.InstancedBufferAttribute(a, n); b.setUsage(THREE.DynamicDrawUsage); return b; };
    g.setAttribute('iPosSize', mk(this.ps, 4)); g.setAttribute('iRot', mk(this.rot, 1));
    g.setAttribute('iCol', mk(this.col, 4)); g.setAttribute('iDat', mk(this.dat, 4));
    this.geo = g;
    const dummy = new THREE.DataTexture(new Float32Array(4), 2, 2, THREE.RedFormat, THREE.FloatType);
    dummy.needsUpdate = true;
    this.uniforms = { uH: { value: dummy }, uHBox: { value: new THREE.Vector4(0, 0, 1, 1) }, uHRes: { value: new THREE.Vector2(2, 2) }, uHOn: { value: 0 },
      // atlas v2 decal art for scorch, frost, poison, crack (R cover, G shade, B glow); procedural fallback when absent
      uMap: { value: tex }, uDUV: { value: [0, 1, 2, 3].map(i => rects[i] ?? new THREE.Vector4()) }, uDOn: { value: tex && rects.length === 4 && rects.every(Boolean) ? 1 : 0 } };
    this.mat = new THREE.ShaderMaterial({
      vertexShader: DECAL_VERT, fragmentShader: DECAL_FRAG, uniforms: this.uniforms,
      transparent: true, depthWrite: false, depthTest: true,
      blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4,
    });
    this.mesh = new THREE.Mesh(g, this.mat);
    this.mesh.frustumCulled = false; this.mesh.renderOrder = 5; this.mesh.visible = false;
    scene.add(this.mesh);
    this.live = []; this.free = [];
  }

  /** Samples `heightAt(x, z)` over [x0, x1] × [z0, z1] at `res` samples a unit into the float texture. */
  setHeight(heightAt, x0, z0, x1, z1, res = 4) {
    const w = Math.max(2, Math.min(256, Math.ceil((x1 - x0) * res) + 1)), h = Math.max(2, Math.min(256, Math.ceil((z1 - z0) * res) + 1));
    const sx = (w - 1) / (x1 - x0), sz = (h - 1) / (z1 - z0);
    const data = new Float32Array(w * h);
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) data[j * w + i] = heightAt(x0 + i / sx, z0 + j / sz);
    const tex = new THREE.DataTexture(data, w, h, THREE.RedFormat, THREE.FloatType);
    tex.minFilter = THREE.NearestFilter; tex.magFilter = THREE.NearestFilter; tex.generateMipmaps = false; tex.needsUpdate = true;
    this.uniforms.uH.value.dispose();
    this.uniforms.uH.value = tex;
    this.uniforms.uHBox.value.set(x0, z0, sx, sz);
    this.uniforms.uHRes.value.set(w, h);
    this.uniforms.uHOn.value = 1;
  }

  add(x, y, z, size, rot, type, color, alpha, bright, life, hold) {
    let d;
    if (this.live.length >= DECALS) {                   // recycle the one closest to gone
      let best = 0, left = Infinity;
      for (let i = 0; i < this.live.length; i++) { const l = this.live[i].life - this.live[i].age; if (l < left) { left = l; best = i; } }
      d = this.live[best];
    } else { d = this.free.pop() || {}; this.live.push(d); }
    d.x = x; d.y = y; d.z = z; d.size = size; d.rot = rot; d.type = type; d.r = color[0]; d.g = color[1]; d.b = color[2];
    d.alpha = alpha; d.bright = bright; d.life = Math.max(0.05, life); d.hold = Math.min(hold, d.life); d.age = 0; d.spin = 0; d.fadeIn = 0.04;
    return d;
  }

  update(dt) {
    const L = this.live; let w = 0;
    for (let i = 0; i < L.length; i++) {
      const d = L[i]; d.age += dt;
      if (d.age >= d.life) { this.free.push(d); continue; }
      L[w++] = d;
    }
    L.length = w;
    for (let i = 0; i < w; i++) {
      const d = L[i];
      const fade = d.age < d.hold ? 1 : 1 - (d.age - d.hold) / Math.max(1e-3, d.life - d.hold);
      const a = d.alpha * Math.min(1, d.age / d.fadeIn) * fade;
      d.rot += d.spin * dt;
      this.ps[i * 4] = d.x; this.ps[i * 4 + 1] = d.y; this.ps[i * 4 + 2] = d.z; this.ps[i * 4 + 3] = d.size;
      this.rot[i] = d.rot;
      this.col[i * 4] = d.r; this.col[i * 4 + 1] = d.g; this.col[i * 4 + 2] = d.b; this.col[i * 4 + 3] = 1;
      this.dat[i * 4] = d.type; this.dat[i * 4 + 1] = d.age / d.life; this.dat[i * 4 + 2] = a; this.dat[i * 4 + 3] = d.bright;
    }
    const g = this.geo;
    g.attributes.iPosSize.needsUpdate = true; g.attributes.iRot.needsUpdate = true; g.attributes.iCol.needsUpdate = true; g.attributes.iDat.needsUpdate = true;
    g.instanceCount = w;
    this.mesh.visible = w > 0;
  }

  dispose(scene) { scene.remove(this.mesh); this.geo.dispose(); this.mat.dispose(); this.uniforms.uH.value.dispose(); }
}

// ---------------------------------------------------------------- ribbon trails (vfx.md F4)
// Every projectile ribbon lives in ONE dynamic geometry (one draw): up to RIB_MAX ribbons of RIB_PTS points, each a
// camera-facing strip textured with the atlas `trail` strip, fading from the head to the tail. A ribbon records a
// new point every RIB_SEG units and drags its head point along with the round in between, so it is smooth at any
// frame rate. Unused slots are collapsed to zero area. transparent + depthWrite:false (kept out of the AO prepass).
const RIB_MAX = 24, RIB_PTS = 12, RIB_SEG = 0.07;
export const RIBBON = {
  archer:   { width: 0.10, color: [1, 0.96, 0.82], bright: 1.4 },
  ballista: { width: 0.12, color: [1, 0.96, 0.82], bright: 1.5 },
  // the element ribbons are kept dim: summed with the trail glows under bloom, a 1.7-bright frost ribbon drew the shot
  // as one white smear from the crystal to the target — the owner's "the frost tower just seems to flash"
  frost:    { width: 0.08, color: [0.45, 0.8, 1], bright: 0.9 },
  fire:     { width: 0.12, color: [1, 0.34, 0.06], bright: 1.1 },   // a deep orange wake; at 1.5 it drew a pale streak
  poison:   { width: 0.11, color: [0.4, 0.9, 0.25], bright: 1.1 },
};

class RibbonLayer {
  constructor(scene, tex, uvTrail) {
    const nv = RIB_MAX * RIB_PTS * 2;
    this.posA = new THREE.BufferAttribute(new Float32Array(nv * 3), 3); this.posA.setUsage(THREE.DynamicDrawUsage);
    this.colA = new THREE.BufferAttribute(new Float32Array(nv * 4), 4); this.colA.setUsage(THREE.DynamicDrawUsage);
    const uvs = new Float32Array(nv * 2), idx = [];
    for (let r = 0; r < RIB_MAX; r++) for (let i = 0; i < RIB_PTS; i++) {
      const k = (r * RIB_PTS + i) * 2, u = uvTrail[0] + uvTrail[2] * (i / (RIB_PTS - 1));
      uvs[k * 2] = u; uvs[k * 2 + 1] = uvTrail[1] + uvTrail[3]; uvs[k * 2 + 2] = u; uvs[k * 2 + 3] = uvTrail[1];
      if (i > 0) idx.push(k - 2, k - 1, k, k - 1, k + 1, k);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', this.posA); g.setAttribute('color', this.colA); g.setAttribute('uv', new THREE.BufferAttribute(uvs, 2)); g.setIndex(idx);
    this.geo = g;
    this.mat = new THREE.MeshBasicMaterial({ map: tex, vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.mesh = new THREE.Mesh(g, this.mat); this.mesh.frustumCulled = false; this.mesh.renderOrder = 19; this.mesh.visible = false;
    scene.add(this.mesh);
    this.slots = [];
    for (let r = 0; r < RIB_MAX; r++) this.slots.push({ on: false, ending: false, fade: 1, n: 0, pts: new Float32Array(RIB_PTS * 3), width: 0.1, seg: RIB_SEG, r: 1, g: 1, b: 1 });
  }
  start(pos, spec) {
    let s = null;
    for (const x of this.slots) if (!x.on) { s = x; break; }
    if (!s) return null;
    s.on = true; s.ending = false; s.fade = 1; s.n = 2; s.width = spec.width; s.seg = spec.seg ?? RIB_SEG;
    s.r = spec.color[0] * spec.bright; s.g = spec.color[1] * spec.bright; s.b = spec.color[2] * spec.bright;
    for (let i = 0; i < 2; i++) { s.pts[i * 3] = pos.x; s.pts[i * 3 + 1] = pos.y; s.pts[i * 3 + 2] = pos.z; }
    return s;
  }
  /** Moves the head to `pos`; lays down a new point once the head is RIB_SEG past the last one. */
  push(s, pos) {
    if (!s || !s.on || s.ending) return;
    const P = s.pts, h = (s.n - 1) * 3, p = (s.n - 2) * 3;
    P[h] = pos.x; P[h + 1] = pos.y; P[h + 2] = pos.z;
    const dx = pos.x - P[p], dy = pos.y - P[p + 1], dz = pos.z - P[p + 2];
    if (dx * dx + dy * dy + dz * dz < s.seg * s.seg) return;
    if (s.n < RIB_PTS) s.n++;
    else P.copyWithin(0, 3);                               // drop the oldest point
    const t = (s.n - 1) * 3; P[t] = pos.x; P[t + 1] = pos.y; P[t + 2] = pos.z;
  }
  end(s) { if (s && s.on) s.ending = true; }
  update(dt, camera) {
    camera.getWorldDirection(_cam);
    const pos = this.posA.array, col = this.colA.array;
    let any = false;
    for (let r = 0; r < RIB_MAX; r++) {
      const s = this.slots[r], base = r * RIB_PTS * 2;
      if (s.on && s.ending) { s.fade -= dt / 0.15; if (s.fade <= 0) s.on = false; }
      if (!s.on) { for (let v = 0; v < RIB_PTS * 2; v++) { pos[(base + v) * 3] = pos[(base + v) * 3 + 1] = pos[(base + v) * 3 + 2] = 0; col[(base + v) * 4 + 3] = 0; } continue; }
      any = true;
      const P = s.pts, n = s.n;
      for (let i = 0; i < RIB_PTS; i++) {
        // points past n repeat the head so the strip ends there (zero-area tail of the buffer)
        const j = Math.min(i, n - 1), j0 = Math.max(0, Math.min(j, n - 2));
        _c.set(P[j0 * 3 + 3] - P[j0 * 3], P[j0 * 3 + 4] - P[j0 * 3 + 1], P[j0 * 3 + 5] - P[j0 * 3 + 2]).cross(_cam);
        if (_c.lengthSq() < 1e-10) _c.set(0, 1, 0); else _c.normalize();
        const along = n > 1 ? j / (n - 1) : 1;               // 0 tail … 1 head
        const hw = s.width * 0.5 * (0.25 + 0.75 * along) * (i < n ? 1 : 0);
        const k = (base + i * 2) * 3;
        pos[k] = P[j * 3] + _c.x * hw; pos[k + 1] = P[j * 3 + 1] + _c.y * hw; pos[k + 2] = P[j * 3 + 2] + _c.z * hw;
        pos[k + 3] = P[j * 3] - _c.x * hw; pos[k + 4] = P[j * 3 + 1] - _c.y * hw; pos[k + 5] = P[j * 3 + 2] - _c.z * hw;
        const a = along * along * s.fade, c = (base + i * 2) * 4;
        col[c] = col[c + 4] = s.r; col[c + 1] = col[c + 5] = s.g; col[c + 2] = col[c + 6] = s.b; col[c + 3] = col[c + 7] = a;
      }
    }
    this.posA.needsUpdate = true; this.colA.needsUpdate = true;
    this.mesh.visible = any;
  }
  dispose(scene) { scene.remove(this.mesh); this.geo.dispose(); this.mat.dispose(); }
}

// ---------------------------------------------------------------- bolts (vfx.md F7)
// A bolt is ONE jagged path drawn as three nested ribbons in one mesh (glow, mid, core) — the old code built the glow
// and the core from two independent random paths, so they ran side by side instead of nesting. Every ribbon uses
// the atlas `beam` strip (a soft falloff with a hot centre); the `lightning_*` strips draw a ~6 px line in a 64 px
// strip, which at `width .30` showed about 0.03 units. Meshes are pooled; only positions change on a re-jag.
const BOLT_PTS = 10, BOLT_LAYERS = 3, BOLT_POOL = 24;
const V_BOLT = BOLT_LAYERS * BOLT_PTS * 2;

const LIGHTS = 6;

const rnd = (a, b) => a + Math.random() * (b - a);
const V3 = (x = 0, y = 0, z = 0) => new THREE.Vector3(x, y, z);
const ease = { out: t => 1 - (1 - t) * (1 - t), in: t => t * t, outBack: t => 1 + 2.7 * Math.pow(t - 1, 3) + 1.7 * Math.pow(t - 1, 2) };
const hexRgb = (hex, out) => { out[0] = ((hex >> 16) & 255) / 255; out[1] = ((hex >> 8) & 255) / 255; out[2] = (hex & 255) / 255; return out; };
// scratch, never escapes a call
const _a = V3(), _b = V3(), _c = V3(), _d = V3(), _cam = V3(), _q = new THREE.Quaternion();
/** The cannon blast's aim, kept apart from the _a–_d scratch its building blocks use. */
const _e = V3();
/** The fire shots' own scratch (the fireball's tail, the gout's aim), apart from the _a–_e their building blocks use. */
const _f = V3(), _g = V3();

export class VFX {
  constructor(scene, camera, atlasTex, meta) {
    this.scene = scene; this.camera = camera; this.meta = meta;
    atlasTex.colorSpace = THREE.SRGBColorSpace; atlasTex.flipY = true; atlasTex.generateMipmaps = true;
    atlasTex.minFilter = THREE.LinearMipmapLinearFilter; atlasTex.magFilter = THREE.LinearFilter;
    this.uv = {};
    const [SW, SH] = Array.isArray(meta.size) ? meta.size : [meta.size, meta.size];   // [w, h] since the tier-up strip was appended (tools/art/vfx-merge.py)
    for (const [k, f] of Object.entries(meta.frames)) {
      const [x, y, w, h] = f;
      this.uv[k] = [x / SW, 1 - (y + h) / SH, w / SW, h / SH];
    }
    // Atlas v2: which frames are data frames (and their default ramp), which skip the round mask, and where the
    // ramp rows are. A v1 atlas has none of these, so everything stays on the colour path (-1).
    const rampNames = meta.ramps?.names ?? [];
    this.rampIndex = {}; rampNames.forEach((n, i) => { this.rampIndex[n] = i; });
    this.hasHot = rampNames.length > 0;                     // the procedural fire ramp (FIRE) reads the same data frames
    if (this.hasHot) this.rampIndex.hot = this.hot(1);
    this.frameRamp = {};                                   // frame -> default ramp index (>= 0) or -2 (no mask)
    for (const [f, r] of Object.entries(meta.ramped ?? {})) if (r in this.rampIndex) this.frameRamp[f] = this.rampIndex[r];
    for (const f of meta.nomask ?? []) this.frameRamp[f] = -2;
    const rampUv = new THREE.Vector4(0, 0, 0, 0);
    if (meta.ramps?.rect) {
      const [rx, ry, rw, rh] = meta.ramps.rect;
      rampUv.set((rx + 0.5) / SW, 1 - (ry + rh * 0.5) / SH, (rw - 1) / SW, -rh / SH);
    }
    this.add = new Layer(scene, atlasTex, true, rampUv);
    this.norm = new Layer(scene, atlasTex, false, rampUv);
    const du = n => this.uv[n] ? new THREE.Vector4(...this.uv[n]) : null;
    this.decals = new DecalLayer(scene, atlasTex, [du('decal_scorch'), du('decal_frost'), du('decal_puddle'), du('decal_crack')]);
    this.particles = []; this.timers = []; this.bolts = []; this.beams = []; this.projectiles = [];
    this._charge = null;                                 // the fortress charge running now (baseCharge)
    // the fortress strike's colours (a PALETTE entry's shape); null = the violet-white arcane. A fortress skin sets its
    // own (the Moonstone Keep's cold silver-blue: render/skins/kits/moonstone.ts)
    this.strikePalette = null;
    this.free = []; this._due = [];
    this.stamps = []; this.stampFree = [];
    this.anims = meta.anims;
    this.ribbons = new RibbonLayer(scene, atlasTex, this.uv.trail || this.uv.glow);
    /** Chest-stage shake (chestStage.ts reads `shakeVec`); battle effects feed `trauma` instead — see `kick`. */
    this.shake = 0; this.shakeVec = V3();
    this.trauma = 0;
    this.lod = 1; this.cap = CAP_BY_TIER[1]; this.prio = PRIO.impact;
    this.boltMat = new THREE.MeshBasicMaterial({ map: atlasTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.boltFree = []; this.boltMade = 0; this.beamFree = [];
    this._pts = new Float32Array(BOLT_PTS * 3); this._side = new Float32Array(BOLT_PTS * 3);
    /** Light slots read by render/vfxLights.ts: `on`, world `x y z`, radius `r`, colour `cr cg cb`, intensity `i`, envelope `k`. */
    this.lights = [];
    for (let i = 0; i < LIGHTS; i++) this.lights.push({ on: false, x: 0, y: 0, z: 0, r: 1, cr: 1, cg: 1, cb: 1, i: 1, k: 0, age: 0, life: 1, flicker: false, seed: 0, pulse: 0, rise: false });
  }

  // ---------------------------------------------------------------- core
  /**
   * Adds a particle. The spec object is copied into a recycled particle (no Vector3 per spawn); what is returned is
   * the live particle, or a shared dummy when the budget refused it (see PRIO).
   */
  spawn(p) {
    const n = this.particles.length;
    if (n >= MAX || (n >= this.cap && this.prio < PRIO.hero) || (n >= this.cap * 0.85 && this.prio <= PRIO.trail)) return DROPPED;
    const q = this.free.pop() || { pos: V3(), vel: V3() };
    q.age = 0; q.life = p.life ?? 1; q.pos.copy(p.pos); if (p.vel) q.vel.copy(p.vel); else q.vel.set(0, 0, 0);
    q.gravity = p.gravity ?? 0; q.drag = p.drag ?? 0; q.size0 = p.size0 ?? 0.3; q.size1 = p.size1 ?? q.size0; q.sizeEase = p.sizeEase ?? null;
    q.aspect = p.aspect ?? 1; q.rot = p.rot ?? 0; q.rotVel = p.rotVel ?? 0; q.color0 = p.color0 ?? [1, 1, 1]; q.color1 = p.color1 ?? q.color0;
    q.alpha = p.alpha ?? 1; q.fadeIn = p.fadeIn ?? 0.05; q.fadeOut = p.fadeOut ?? 0.5; q.additive = p.additive ?? true;
    q.stretch = p.stretch ?? 0; q.frame = p.frame ?? 'glow'; q.anim = p.anim ?? null; q.floor = p.floor ?? -Infinity;
    q.bright = p.bright ?? 1; q.flat = p.flat ?? false; q.align = p.align ?? false;
    q.ramp = typeof p.ramp === 'number' ? p.ramp : p.ramp != null ? (this.rampIndex[p.ramp] ?? null) : null; q.animRate = p.animRate ?? 0;
    q.swirl = p.swirl ?? 0;                                // rad/s the horizontal velocity turns about the vertical: a helix (killFx's Ember Wisps)
    this.particles.push(q); return q;
  }
  /** The hot ramp (FIRE) at `heat` (0–1], for a spawn's or stamp's `ramp`; null on an atlas without ramps. */
  hot(heat = 1) { return this.hasHot ? HOT_RAMP + Math.min(1, Math.max(0.01, heat)) : null; }
  /** `n` scaled by the quality tier's `lod` (a single spawn stays single). */
  burst(n, fn) { const m = n <= 1 ? n : Math.max(1, Math.round(n * this.lod)); for (let i = 0; i < m; i++) this.spawn(fn(i)); }
  later(t, fn) { this.timers.push({ t, fn }); }
  /** Looping flipbook: frame `i` of `name` (wraps) — a spinning coin, a flame. */
  frameLoop(name, i) { const a = this.anims[name]; if (!a) return 'glow'; return a[((Math.floor(i) % a.length) + a.length) % a.length]; }
  frameAt(name, t) { const a = this.anims[name]; if (!a) return 'glow'; let i = Math.floor(t * a.length); if (!(i >= 0)) i = 0; if (i > a.length - 1) i = a.length - 1; return a[i]; }

  /** Camera trauma for the battle (vfx.md F1). Adds, clamped to 1; the client drains it with `takeTrauma` each frame. */
  kick(t) { this.trauma = Math.min(1, this.trauma + t); }
  takeTrauma() { const t = this.trauma; this.trauma = 0; return t; }

  /** Quality tier index (render/quality.ts: 0 ultra … 4 lowest) → burst `lod` and the live-particle `cap`. */
  setQuality(tier) {
    const i = Math.max(0, Math.min(LOD_BY_TIER.length - 1, tier | 0));
    this.lod = LOD_BY_TIER[i]; this.cap = CAP_BY_TIER[i];
  }

  /** The level's height field for the decal layer: `heightAt(x, z)` sampled over the given world rectangle. */
  setGround(heightAt, x0, z0, x1, z1, res = 4) { this.decals.setHeight(heightAt, x0, z0, x1, z1, res); }

  /**
   * A VFX light slot (vfx.md F5.1) — not a THREE.PointLight: render/vfxLights.ts feeds the live slots to the HQ and
   * ground shaders as six uniforms. Recycles a free slot or the one nearest its end. The returned slot's `x y z` may
   * be moved while it lives (a fireball's light follows it).
   */
  light(pos, color, radius = 2, intensity = 2, life = 0.25, { flicker = false, pulse = 0, rise = false } = {}) {
    let s = null, most = -1;
    for (let i = 0; i < LIGHTS; i++) { const l = this.lights[i]; if (!l.on) { s = l; break; } const f = l.age / l.life; if (f > most) { most = f; s = l; } }
    s.on = true; s.x = pos.x; s.y = pos.y; s.z = pos.z; s.r = radius; s.cr = color[0]; s.cg = color[1]; s.cb = color[2];
    s.i = intensity; s.age = 0; s.life = Math.max(0.03, life); s.flicker = flicker; s.seed = Math.random() * 6.28; s.k = 0;
    s.pulse = pulse; s.rise = rise;
    return s;
  }

  /**
   * A mark on the ground (vfx.md F5.2). `type` is one of DECAL_TYPE's names; `hold` seconds at full strength, then it
   * fades over the rest of `life`. Rings expand over `life`.
   */
  decal(pos, { type = 'scorch', size = 1, life = 2, hold = 0, color = [0.12, 0.1, 0.09], alpha = 1, bright = 1, rot = rnd(0, 6.283), spin = 0, fadeIn = 0.04 } = {}) {
    const d = this.decals.add(pos.x, pos.y, pos.z, size, rot, DECAL_TYPE[type] ?? 1, color, alpha, bright, life, hold);
    d.spin = spin; d.fadeIn = Math.max(0.01, fadeIn);
    return d;
  }

  /**
   * A ribbon trail behind a projectile (RIBBON has the kinds that get one; null for the rest or when all 24 are in
   * use). Call `ribbonPush(r, pos)` every frame while it flies and `ribbonEnd(r)` when it lands (a 0.15 s fade).
   */
  ribbon(kind, pos) { const spec = RIBBON[kind]; return spec ? this.ribbons.start(pos, spec) : null; }
  /** A ribbon of the caller's own spec ({ width, color, bright, seg? }: `seg` the point spacing, default RIB_SEG): a
   *  troop skin's weapon trail (render/skins/kit/troopFx.ts). Same pool and calls as `ribbon`. */
  ribbonWith(spec, pos) { return this.ribbons.start(pos, spec); }
  ribbonPush(r, pos) { this.ribbons.push(r, pos); }
  ribbonEnd(r) { this.ribbons.end(r); }

  update(dt) {
    if (!(dt > 0)) dt = 1 / 60; dt = Math.min(dt, 0.05);
    this.prio = PRIO.impact;
    // timers: collect the due ones first, then run them (a timer may schedule more)
    const T = this.timers, due = this._due; let w = 0;
    for (let i = 0; i < T.length; i++) { const tm = T[i]; tm.t -= dt; if (tm.t <= 0) due.push(tm); else T[w++] = tm; }
    T.length = w;
    for (let i = 0; i < due.length; i++) due[i].fn();
    due.length = 0;
    const P = this.projectiles; w = 0;
    for (let i = 0; i < P.length; i++) { const pr = P[i]; pr.age += dt; if (pr.age >= pr.life) due.push(pr); else P[w++] = pr; }
    P.length = w;
    for (let i = 0; i < due.length; i++) due[i].onHit();
    due.length = 0;
    for (let i = 0; i < P.length; i++) P[i].tick(dt);
    // bolts and beams (pooled)
    const B = this.bolts; w = 0;
    for (let i = 0; i < B.length; i++) { const b = B[i]; b.age += dt; if (b.age >= b.life) this._freeBolt(b); else B[w++] = b; }
    B.length = w;
    for (let i = 0; i < w; i++) {
      const b = B[i];
      b.rebuild += dt; if (b.rebuild > 0.045) { b.rebuild = 0; this._buildBolt(b); }
      b.mesh.material.opacity = 1 - Math.pow(b.age / b.life, 2);
    }
    const M = this.beams; w = 0;
    for (let i = 0; i < M.length; i++) { const b = M[i]; b.age += dt; if (b.age >= b.life) { b.mesh.visible = false; this.beamFree.push(b); } else M[w++] = b; }
    M.length = w;
    for (let i = 0; i < w; i++) { const b = M[i]; const t = b.age / b.life; b.mesh.scale.y = b.width * (1 - t * 0.85); b.mesh.material.opacity = 1 - t * t; }
    // light envelopes: a 8 % attack, then a squared fall; `flicker` jitters a strike's slot
    for (let i = 0; i < LIGHTS; i++) {
      const l = this.lights[i]; if (!l.on) continue;
      l.age += dt; if (l.age >= l.life) { l.on = false; l.k = 0; continue; }
      const t = l.age / l.life;
      // `rise`: builds up over the life and cuts at the end (a charge); otherwise a fast attack and a squared fall
      let k = l.rise ? t * t * (t < 0.95 ? 1 : (1 - t) / 0.05) : t < 0.08 ? t / 0.08 : Math.pow(1 - (t - 0.08) / 0.92, 2);
      if (l.pulse) k *= 0.6 + 0.4 * Math.sin(l.age * l.pulse * 6.283);
      if (l.flicker) k *= 0.55 + 0.45 * Math.abs(Math.sin(l.age * 70 + l.seed) * Math.sin(l.age * 23 + l.seed * 2));
      l.k = k;
    }
    this.decals.update(dt);
    this.ribbons.update(dt, this.camera);
    // particles: compacted in place, dead ones back to the free list
    this.add.begin(); this.norm.begin();
    const S = this.particles; w = 0;
    for (let i = 0; i < S.length; i++) {
      const p = S[i];
      p.age += dt; if (!(p.age < p.life)) { this.free.push(p); continue; }
      S[w++] = p;
      const t = p.age / p.life;
      p.vel.y -= p.gravity * dt; if (p.drag) p.vel.multiplyScalar(1 - p.drag * dt);
      if (p.swirl) { const sa = p.swirl * dt, sc = Math.cos(sa), ss = Math.sin(sa), vx = p.vel.x; p.vel.x = vx * sc - p.vel.z * ss; p.vel.z = vx * ss + p.vel.z * sc; }
      p.pos.addScaledVector(p.vel, dt);
      if (p.pos.y < p.floor) { p.pos.y = p.floor; p.vel.y *= -0.35; p.vel.x *= 0.7; p.vel.z *= 0.7; }
      p.rot += p.rotVel * dt;
      const size = p.size0 + (p.size1 - p.size0) * (p.sizeEase ? p.sizeEase(t) : t);
      const a = p.alpha * Math.min(1, p.age / Math.max(1e-3, p.fadeIn * p.life)) * Math.min(1, (p.life - p.age) / Math.max(1e-3, p.fadeOut * p.life));
      const c0 = p.color0, c1 = p.color1, br = p.bright;
      const r = c0[0] + (c1[0] - c0[0]) * t, g = c0[1] + (c1[1] - c0[1]) * t, b = c0[2] + (c1[2] - c0[2]) * t;
      const frame = p.anim ? (p.animRate ? this.frameLoop(p.anim, p.age * p.animRate) : this.frameAt(p.anim, t)) : p.frame;
      const uv = this.uv[frame] || this.uv.glow;
      const fr = this.frameRamp[frame], ramp = fr === undefined ? -1 : fr >= 0 && p.ramp !== null ? p.ramp : fr;
      let wd = size, h = size * p.aspect, ax = 0, ay = 0, az = 0;
      if (p.stretch || p.align) { const sp = p.vel.length(); wd = size * (1 + sp * p.stretch); ax = p.vel.x; ay = p.vel.y; az = p.vel.z; }
      if (p.flat) { ax = 0; ay = 2; az = 0; }
      (p.additive ? this.add : this.norm).push(p, p.pos.x, p.pos.y, p.pos.z, wd, h, p.rot, r * br, g * br, b * br, a, uv, ax, ay, az, ramp);
    }
    S.length = w;
    this._drawStamps();
    this.add.end(this.camera); this.norm.end(this.camera);
    // chest-stage camera shake (the battle uses trauma → IsoCamera)
    if (this.shake > 0) { this.shake = Math.max(0, this.shake - dt * 3); const s = this.shake * this.shake * 0.08; this.shakeVec.set(rnd(-s, s), rnd(-s, s), 0); } else this.shakeVec.set(0, 0, 0);
  }

  /** Removes every mesh this module added to the scene (the level is over). */
  dispose() {
    const sc = this.scene;
    sc.remove(this.add.mesh, this.norm.mesh);
    this.add.geo.dispose(); this.norm.geo.dispose(); this.add.mat.dispose(); this.norm.mat.dispose();
    this.decals.dispose(sc);
    this.ribbons.dispose(sc);
    for (const b of this.bolts) this.boltFree.push(b);
    for (const b of this.boltFree) { sc.remove(b.mesh); b.mesh.geometry.dispose(); b.mesh.material.dispose(); }
    for (const b of this.beams) this.beamFree.push(b);
    for (const b of this.beamFree) { sc.remove(b.mesh); b.mesh.geometry.dispose(); b.mesh.material.dispose(); }
    this.bolts.length = 0; this.beams.length = 0; this.boltFree.length = 0; this.beamFree.length = 0;
    this.timers.length = 0; this.projectiles.length = 0; this.particles.length = 0; this.stamps.length = 0;
    this._charge = null;
    for (const l of this.lights) { l.on = false; l.k = 0; }
  }

  // ---------------------------------------------------------------- bolts & beams
  _newBolt() {
    const g = new THREE.BufferGeometry();
    const pos = new THREE.BufferAttribute(new Float32Array(V_BOLT * 3), 3); pos.setUsage(THREE.DynamicDrawUsage);
    const col = new THREE.BufferAttribute(new Float32Array(V_BOLT * 3), 3); col.setUsage(THREE.DynamicDrawUsage);
    const uvs = new Float32Array(V_BOLT * 2), idx = [];
    const beam = this.uv.beam || this.uv.glow, soft = this.uv.glow;
    for (let L = 0; L < BOLT_LAYERS; L++) {
      // The outer glow reads the CENTRE COLUMN of the round `glow` sprite: its falloff across the width reaches 0 at
      // the edges, where the `beam` strip still has alpha 9–18 — on a short, wide arc that drew as a pale rectangle.
      const s = L === 0 ? soft : beam;
      for (let i = 0; i < BOLT_PTS; i++) {
        const k = (L * BOLT_PTS + i) * 2, u = L === 0 ? s[0] + s[2] * 0.5 : s[0] + s[2] * (0.08 + 0.84 * i / (BOLT_PTS - 1));
        uvs[k * 2] = u; uvs[k * 2 + 1] = s[1] + s[3]; uvs[k * 2 + 2] = u; uvs[k * 2 + 3] = s[1];
        if (i > 0) idx.push(k - 2, k - 1, k, k - 1, k + 1, k);
      }
    }
    g.setAttribute('position', pos); g.setAttribute('color', col); g.setAttribute('uv', new THREE.BufferAttribute(uvs, 2)); g.setIndex(idx);
    const mat = this.boltMat.clone(); mat.vertexColors = true;
    const mesh = new THREE.Mesh(g, mat); mesh.frustumCulled = false; mesh.renderOrder = 25; mesh.visible = false;
    this.scene.add(mesh);
    this.boltMade++;
    return { mesh, from: V3(), to: V3(), life: 1, age: 0, rebuild: 0, width: 0.16, w0: 0, w1: 0, w2: 0, jag: 1 };
  }
  _freeBolt(b) { b.mesh.visible = false; this.boltFree.push(b); }
  _takeBolt(ambient) {
    const b = this.boltFree.pop();
    if (b) return b;
    if (this.boltMade < BOLT_POOL) return this._newBolt();
    if (ambient) return null;                              // idle arcs never steal a live bolt
    return this.bolts.shift() ?? null;                     // steal the oldest
  }

  /** Re-jags the shared path and writes the three ribbons around it. */
  _buildBolt(b) {
    const a = b.from, c = b.to, P = this._pts, S = this._side;
    _a.subVectors(c, a); const len = _a.length(); if (len < 1e-4) return; _a.multiplyScalar(1 / len);
    _b.set(-_a.z, 0, _a.x); if (_b.lengthSq() < 1e-6) _b.set(1, 0, 0); _b.normalize();
    for (let i = 0; i < BOLT_PTS; i++) {
      const t = i / (BOLT_PTS - 1), mid = 1 - Math.abs(t * 2 - 1);
      const j1 = (i === 0 || i === BOLT_PTS - 1) ? 0 : rnd(-1, 1) * 0.14 * mid * len * b.jag, j2 = (i === 0 || i === BOLT_PTS - 1) ? 0 : rnd(-1, 1) * 0.1 * mid * len * b.jag;
      P[i * 3] = a.x + _a.x * len * t + _b.x * j1; P[i * 3 + 1] = a.y + _a.y * len * t + j2; P[i * 3 + 2] = a.z + _a.z * len * t + _b.z * j1;
    }
    this.camera.getWorldDirection(_cam);
    for (let i = 0; i < BOLT_PTS; i++) {
      const i0 = i < BOLT_PTS - 1 ? i : i - 1;
      _c.set(P[i0 * 3 + 3] - P[i0 * 3], P[i0 * 3 + 4] - P[i0 * 3 + 1], P[i0 * 3 + 5] - P[i0 * 3 + 2]).cross(_cam);
      if (_c.lengthSq() < 1e-8) _c.copy(_b); _c.normalize();
      S[i * 3] = _c.x; S[i * 3 + 1] = _c.y; S[i * 3 + 2] = _c.z;
    }
    const pos = b.mesh.geometry.attributes.position, arr = pos.array, W = [b.w0, b.w1, b.w2];
    for (let L = 0; L < BOLT_LAYERS; L++) {
      for (let i = 0; i < BOLT_PTS; i++) {
        const t = i / (BOLT_PTS - 1), taper = 0.3 + 0.7 * Math.sin(Math.PI * Math.min(1, 0.1 + t * 0.9));
        const hw = W[L] * 0.5 * taper, k = ((L * BOLT_PTS + i) * 2) * 3;
        arr[k] = P[i * 3] + S[i * 3] * hw; arr[k + 1] = P[i * 3 + 1] + S[i * 3 + 1] * hw; arr[k + 2] = P[i * 3 + 2] + S[i * 3 + 2] * hw;
        arr[k + 3] = P[i * 3] - S[i * 3] * hw; arr[k + 4] = P[i * 3 + 1] - S[i * 3 + 1] * hw; arr[k + 5] = P[i * 3 + 2] - S[i * 3 + 2] * hw;
      }
    }
    pos.needsUpdate = true;
  }

  /**
   * A jagged bolt from `from` to `to`: one path, three nested ribbons. `width` is the mid ribbon; `glow` (default
   * 2.6× width) and `core` (default 0.3× width, `coreBright` 3, white) can be set for the fortress's 0.9 / 0.3 / 0.08.
   * `ambient` bolts are dropped rather than steal a pooled mesh.
   */
  lightning(from, to, { life = 0.28, width = 0.16, color = PALETTE.arcane.hex, glow, core, coreBright = 3, jag = 1, ambient = false, xray = false } = {}) {
    const b = this._takeBolt(ambient);
    if (!b) return null;
    const len = from.distanceTo(to);
    b.from.copy(from); b.to.copy(to); b.life = life; b.age = 0; b.rebuild = 0; b.width = width; b.jag = jag;
    // a ribbon wider than a good fraction of its length is a quad, not a bolt: cap the widths by the length
    b.w0 = Math.min(glow ?? width * 2.6, len * 0.6); b.w1 = Math.min(width, len * 0.35); b.w2 = Math.min(core ?? width * 0.3, len * 0.2);
    const rgb = typeof color === 'number' ? hexRgb(color, [0, 0, 0]) : color;
    const col = b.mesh.geometry.attributes.color, arr = col.array;
    const layer = [[rgb[0] * 0.45, rgb[1] * 0.45, rgb[2] * 0.45], [rgb[0] * 1.2, rgb[1] * 1.2, rgb[2] * 1.2], [coreBright, coreBright, coreBright]];
    for (let L = 0; L < BOLT_LAYERS; L++) for (let i = 0; i < BOLT_PTS; i++) {
      // the glow fades to nothing at both ends (no hard end cap); mid and core keep 35 % so they meet their anchors
      const e = Math.sin(Math.PI * i / (BOLT_PTS - 1)), f = L === 0 ? Math.pow(e, 0.7) : 0.35 + 0.65 * Math.min(1, e * 2.5);
      for (let side = 0; side < 2; side++) { const k = ((L * BOLT_PTS + i) * 2 + side) * 3; arr[k] = layer[L][0] * f; arr[k + 1] = layer[L][1] * f; arr[k + 2] = layer[L][2] * f; }
    }
    col.needsUpdate = true;
    // `xray`: drawn over walls and trees (the fortress strike must read even when its target is behind the keep)
    b.mesh.material.depthTest = !xray;
    b.mesh.material.opacity = 1; b.mesh.visible = true;
    this._buildBolt(b);
    this.bolts.push(b);
    return b;
  }

  beam(from, to, { life = 0.22, width = 0.18, color = 0x9fe8ff } = {}) {
    let b = this.beamFree.pop();
    if (!b) {
      if (this.beams.length >= 8) b = this.beams.shift();
      else {
        const g = new THREE.PlaneGeometry(1, 1);
        const uvs = g.attributes.uv, s = this.uv.beam;
        for (let i = 0; i < uvs.count; i++) uvs.setXY(i, s[0] + uvs.getX(i) * s[2], s[1] + uvs.getY(i) * s[3]);
        const m = new THREE.Mesh(g, this.boltMat.clone()); m.frustumCulled = false; m.renderOrder = 25;
        this.scene.add(m);
        b = { mesh: m, age: 0, life: 1, width: 1 };
      }
    }
    const m = b.mesh, len = from.distanceTo(to);
    m.material.color.set(color); m.material.opacity = 1; m.visible = true;
    m.position.copy(from).lerp(to, 0.5);
    _a.subVectors(to, from).normalize();
    this.camera.getWorldDirection(_cam);
    _q.setFromUnitVectors(_b.set(1, 0, 0), _a);
    m.quaternion.copy(_q);
    // roll so the plane's normal faces the camera as much as possible
    _c.set(0, 0, 1).applyQuaternion(_q); _d.set(0, 1, 0).applyQuaternion(_q);
    _cam.negate().projectOnPlane(_a).normalize();
    m.rotateX(Math.atan2(_cam.dot(_d), _cam.dot(_c)));
    m.scale.set(len, width, 1);
    b.age = 0; b.life = life; b.width = width;
    this.beams.push(b); return b;
  }

  // ---------------------------------------------------------------- building blocks
  flash(pos, size = 0.6, color = [1, 0.85, 0.5]) {
    this.spawn({ pos, anim: 'flash', life: 0.14, size0: size * 0.6, size1: size, fadeIn: 0, fadeOut: 0.4, color0: color, bright: 2.2, rot: rnd(0, 6.3) });
    this.spawn({ pos, frame: 'glow', life: 0.18, size0: size * 1.4, size1: size * 0.6, fadeIn: 0, fadeOut: 0.9, color0: color, bright: 1.6 });
  }
  sparks(pos, n, { color = [1, 0.8, 0.4], speed = 3, up = 2, gravity = 7, life = 0.5, size = 0.16, floor } = {}) {
    this.burst(n, () => ({ pos, frame: 'spark', vel: _d.set(rnd(-1, 1), rnd(0.2, 1) * up, rnd(-1, 1)).normalize().multiplyScalar(rnd(0.4, 1) * speed), gravity, drag: 1.2, life: life * rnd(0.6, 1.2), size0: size, size1: size * 0.3, aspect: 0.35, stretch: 0.25, color0: [1, 1, 1], color1: color, bright: 2.0, fadeIn: 0, fadeOut: 0.5, floor }));
  }
  /**
   * A shock ring. Flat rings lie on the ground-decal layer (they follow the slope — vfx.md F5.2); upright ones
   * (`flat: false`, e.g. the fortress wind-up) stay camera-facing sprites.
   */
  ring(pos, { size = 1.4, life = 0.35, color = [1, 0.9, 0.7], flat = true, bright = 2.2 } = {}) {
    if (flat) return this.decal(pos, { type: 'ring', size, life, color, bright, alpha: 1 });
    _a.copy(pos); _a.y += 0.03;
    const p = this.spawn({ pos: _a, anim: 'shock', life, size0: size * 0.3, size1: size, sizeEase: ease.out, fadeIn: 0, fadeOut: 0.6, color0: color, bright });
    this.spawn({ pos: _a, frame: 'glow', life: life * 0.7, size0: size * 0.6, size1: size * 0.2, fadeIn: 0, fadeOut: 0.9, color0: color, bright: 1.2 });
    return p;
  }
  smoke(pos, n, { size = 0.6, life = 1.4, rise = 0.8, spread = 0.6, color = [0.9, 0.9, 0.95], alpha = 0.85 } = {}) {
    this.burst(n, () => ({ pos: _c.copy(pos).add(_d.set(rnd(-0.1, 0.1), rnd(0, 0.15), rnd(-0.1, 0.1))), anim: 'smoke', vel: _b.set(rnd(-1, 1) * spread, rnd(0.5, 1) * rise, rnd(-1, 1) * spread), drag: 1.5, life: life * rnd(0.7, 1.2), size0: size * 0.5, size1: size * 1.6, sizeEase: ease.out, color0: color, alpha, fadeIn: 0.05, fadeOut: 0.6, additive: false, rot: rnd(0, 6.3), rotVel: rnd(-1, 1) }));
  }
  /**
   * Soot off fire (FIRE.soot…): dark and thin, easing in, spreading and thinning out as it rises — the pale `smoke`
   * default drew fire's smoke as cotton balls.
   */
  fireSmoke(pos, n, { size = 0.4, life = 1.2, rise = 0.8, spread = 0.3 } = {}) {
    const F = FIRE;
    this.burst(n, () => ({ pos: _c.copy(pos).add(_d.set(rnd(-0.08, 0.08), rnd(0, 0.12), rnd(-0.08, 0.08))), anim: 'smoke', vel: _b.set(rnd(-1, 1) * spread, rnd(0.6, 1) * rise, rnd(-1, 1) * spread), drag: 1.2, life: life * rnd(0.75, 1.25), size0: size * 0.4, size1: size * 0.4 * F.sootGrow, sizeEase: ease.out, color0: F.soot, alpha: F.sootAlpha, fadeIn: F.sootIn, fadeOut: F.sootOut, additive: false, rot: rnd(0, 6.3), rotVel: rnd(-0.6, 0.6) }));
  }
  debris(pos, n, { speed = 3, size = 0.12, color = [1, 1, 1], kind } = {}) {
    // atlas v2: the kinetic clod colour gets real painted clods (brown, one with a grass tuft) and `kind: 'ice'` gets
    // ice shards — both carry their own colour, so the tint goes white. A v1 atlas keeps the tinted rock chip.
    if (!kind && color === PALETTE.kinetic.clod) kind = 'clod';
    const set = kind && this.uv[kind + '_a'] ? [kind + '_a', kind + '_b', kind + '_c'] : null;
    if (set) { color = [1, 1, 1]; size *= 1.35; }
    this.burst(n, () => ({ pos, frame: set ? set[(Math.random() * 3) | 0] : 'debris', vel: _d.set(rnd(-1, 1), rnd(0.8, 1.6), rnd(-1, 1)).multiplyScalar(speed * rnd(0.5, 1)), gravity: 9, drag: 0.3, life: rnd(0.6, 1.1), size0: size * rnd(0.6, 1.4), size1: size * rnd(0.6, 1.4), color0: color, additive: false, rot: rnd(0, 6.3), rotVel: rnd(-12, 12), fadeIn: 0, fadeOut: 0.25, floor: pos.y }));
  }
  dust(pos, n, { size = 0.5, speed = 1.6, color = [1, 1, 1], life = 0.65 } = {}) {
    const m = n <= 1 ? n : Math.max(1, Math.round(n * this.lod));
    for (let i = 0; i < m; i++) {
      const a = i / m * 6.283;
      this.spawn({ pos: _c.copy(pos).add(_d.set(0, 0.03, 0)), frame: 'dust', vel: _b.set(Math.cos(a), 0.15, Math.sin(a)).multiplyScalar(speed * rnd(0.7, 1.1)), drag: 2.5, life: life * rnd(0.8, 1.2), size0: size * 0.5, size1: size * 1.3, color0: color, alpha: 0.7, additive: false, fadeIn: 0, fadeOut: 0.6, rot: rnd(0, 6.3) });
    }
  }
  sparkles(pos, n, { color = [1, 0.85, 0.35], radius = 0.35, rise = 1.2, life = 0.9, size = 0.18 } = {}) {
    this.burst(n, () => ({ pos: _c.copy(pos).add(_d.set(rnd(-radius, radius), rnd(0, 0.3), rnd(-radius, radius))), frame: 'star', vel: _b.set(rnd(-0.2, 0.2), rnd(0.5, 1) * rise, rnd(-0.2, 0.2)), life: life * rnd(0.6, 1.3), size0: 0, size1: size, sizeEase: t => Math.sin(t * Math.PI), color0: color, bright: 2.0, fadeIn: 0.1, fadeOut: 0.3, rot: rnd(0, 6.3), rotVel: rnd(-2, 2) }));
  }
  /** Poison bubbles: rise, wobble and pop (atlas v2 `bubble`; a glow on a v1 atlas). */
  bubbles(pos, n, { color = PALETTE.poison.core, radius = 0.3, size = 0.12, life = 0.8 } = {}) {
    const frame = this.uv.bubble ? 'bubble' : 'glow';
    this.burst(n, () => ({ pos: _c.copy(pos).add(_d.set(rnd(-radius, radius), rnd(0.02, 0.1), rnd(-radius, radius))), frame, vel: _b.set(rnd(-0.1, 0.1), rnd(0.25, 0.5), rnd(-0.1, 0.1)), life: life * rnd(0.6, 1.2), size0: size * 0.4, size1: size * rnd(0.9, 1.4), sizeEase: ease.outBack, color0: color, bright: 1.3, additive: false, fadeIn: 0.1, fadeOut: 0.15 }));
  }
  /**
   * A floating number (vfx.md F9): `+12` gold over a kill, or a crit's damage. Atlas v2 digit glyphs on the normal
   * layer, popping in with outBack and rising. `text` is any string of 0-9, '+' and '!'; a v1 atlas draws nothing.
   */
  number(pos, text, { color = [1, 0.84, 0.3], size = 0.22, life = 0.9, rise = 0.7 } = {}) {
    const dig = this.meta.digits; if (!dig) return;
    const s = String(text), n = s.length, step = size * 0.62;
    this.camera.getWorldDirection(_cam); _q.setFromRotationMatrix(this.camera.matrixWorld);
    const right = _c.set(1, 0, 0).applyQuaternion(_q);
    const prev = this.prio; this.prio = PRIO.hero;         // a number is information, never dropped for budget
    for (let i = 0; i < n; i++) {
      const f = dig[s[i]]; if (!f) continue;
      const off = (i - (n - 1) / 2) * step;
      this.spawn({ pos: _a.copy(pos).addScaledVector(right, off), frame: f, vel: _b.set(0, rise, 0), drag: 1.6, life, size0: 0, size1: size, sizeEase: ease.outBack, color0: color, bright: 1, additive: false, fadeIn: 0.01, fadeOut: 0.3 });
    }
    this.prio = prev;
  }
  glowPulse(pos, { size = 0.5, life = 0.3, color = [0.5, 0.85, 1] } = {}) {
    return this.spawn({ pos, frame: 'glow', life, size0: size * 0.3, size1: size, fadeIn: 0.2, fadeOut: 0.5, color0: color, bright: 2.0 });
  }
  /** Rising, cooling sparks (fire towers' idle, a scorch's aftermath). */
  embers(pos, n, { color = PALETTE.fire.rim, radius = 0.12, life = 1.1, size = 0.07 } = {}) {
    this.burst(n, () => ({ pos: _c.copy(pos).add(_d.set(rnd(-radius, radius), rnd(0, 0.08), rnd(-radius, radius))), frame: 'glow', vel: _b.set(rnd(-0.15, 0.15), rnd(0.35, 0.7), rnd(-0.15, 0.15)), drag: 0.4, life: life * rnd(0.7, 1.2), size0: size, size1: size * 0.3, color0: PALETTE.fire.core, color1: color, bright: 2.2, fadeIn: 0.1, fadeOut: 0.5 }));
  }
  /** A short licking flame (the explosion flipbook, small and fire-ramped). */
  flames(pos, { life = 0.45, size = 0.45, color = PALETTE.fire.core } = {}) {
    if (this.anims.flame) {                                // atlas v2: a looping flame tongue (1:2 cell) on the hot ramp (FIRE)
      this.spawn({ pos: _a.copy(pos).setY(pos.y + size * 0.45), anim: 'flame', animRate: 16, ramp: this.hot(FIRE.heat.lick) ?? 'fire', life, size0: size * 0.45, size1: size * 0.7, aspect: 2, sizeEase: ease.out, vel: _b.set(0, 0.3, 0), fadeIn: 0.1, fadeOut: 0.45, color0: [1, 1, 1], bright: 1.1 });
      return;
    }
    this.spawn({ pos, anim: 'explosion', life, size0: size * 0.5, size1: size, sizeEase: ease.out, vel: _b.set(0, 0.5, 0), fadeIn: 0, fadeOut: 0.4, color0: color, bright: 1.3, rot: rnd(0, 6.3) });
  }
  projectile({ from, to, life, frame = 'orb', size = 0.28, color = [0.6, 0.9, 1], arc = 0, trail = true, onHit, spin = 0 }) {
    const pr = { age: 0, life, from: from.clone(), to: to.clone(), pos: from.clone(), prev: from.clone(), vel: V3(), onHit: () => onHit(pr.pos), tick: (dt) => {
      const t = pr.age / pr.life; pr.prev.copy(pr.pos);
      pr.pos.copy(pr.from).lerp(pr.to, t); pr.pos.y += arc * 4 * t * (1 - t);
      pr.vel.subVectors(pr.pos, pr.prev).multiplyScalar(1 / Math.max(dt, 1e-3));
      (this.add).push(null, pr.pos.x, pr.pos.y, pr.pos.z, size * 1.3, size * 1.3, spin * pr.age, color[0] * 2.4, color[1] * 2.4, color[2] * 2.4, 1, this.uv[frame], 0, 0, 0);
      if (trail) { this.spawn({ pos: pr.pos, frame: 'glow', life: 0.32, size0: size * 1.2, size1: 0, color0: color, bright: 1.6, fadeIn: 0, fadeOut: 1, vel: _b.set(0, 0.15, 0) }); this.spawn({ pos: pr.pos, frame: 'spark', life: 0.12, size0: size * 1.6, size1: size * 0.5, aspect: 0.4, color0: [1, 1, 1], bright: 2.5, fadeIn: 0, fadeOut: 1, stretch: 0.12, vel: _b.copy(pr.vel).multiplyScalar(0.2) }); }
    } };
    this.projectiles.push(pr); return pr;
  }

  // ---------------------------------------------------------------- effects
  cannonFire(muzzle, target, { color = [0.55, 0.85, 1] } = {}) {
    this.flash(muzzle, 0.55, color);
    this.sparks(muzzle, 6, { color, speed: 2.5, up: 1, gravity: 4, life: 0.35, size: 0.12 });
    this.smoke(muzzle, 2, { size: 0.35, life: 0.8, rise: 0.4, spread: 0.3 });
    const d = muzzle.distanceTo(target);
    this.projectile({ from: muzzle, to: target.clone().add(V3(0, 0.12, 0)), life: d / 11, frame: 'orb', size: 0.34, color, onHit: p => this.plasmaImpact(p.clone().setY(target.y), color) });
  }
  plasmaImpact(pos, color = [0.55, 0.85, 1]) {
    this.spawn({ pos: _a.copy(pos).setY(pos.y + 0.25), anim: 'explosion', life: 0.6, size0: 1.0, size1: 1.7, sizeEase: ease.out, fadeIn: 0, fadeOut: 0.35, color0: [1, 1, 1], bright: 1.15, rot: rnd(0, 6.3) });
    this.flash(_a.copy(pos).setY(pos.y + 0.2), 0.9, color);
    this.ring(pos, { size: 2.2, life: 0.45, color });
    this.sparks(_a.copy(pos).setY(pos.y + 0.2), 18, { color, speed: 4.5, up: 2.5, gravity: 9, life: 0.7, size: 0.2, floor: pos.y + 0.02 });
    this.smoke(pos, 4, { size: 0.6, life: 1.4, rise: 0.9 });
    this.debris(pos, 6, { speed: 3, size: 0.11 });
    this.sparkles(pos, 5, { color, radius: 0.3, rise: 1.4, life: 0.6, size: 0.16 });
    this.kick(0.12);
  }
  /**
   * The cannon as a DIRT hit (vfx.md F3): a short hot core, dark smoke, a brown dust skirt on the ground and clods —
   * nothing orange, so it can no longer be mistaken for the fire tower's fireball.
   */
  cannonHit(pos) {
    const K = PALETTE.kinetic;
    const up = _a.copy(pos).setY(pos.y + 0.15);
    this.spawn({ pos: up, frame: 'glow', life: 0.09, size0: 0.95, size1: 0.45, fadeIn: 0, fadeOut: 0.8, color0: K.core, bright: 2.6 });
    this.spawn({ pos: up, anim: 'flash', life: 0.1, size0: 0.4, size1: 0.75, fadeIn: 0, fadeOut: 0.5, color0: K.core, bright: 2.0, rot: rnd(0, 6.3) });
    this.smoke(pos, 4, { size: 0.55, life: 1.1, rise: 0.7, spread: 0.5, color: K.smoke, alpha: 0.75 });
    this.dust(pos, 8, { size: 0.55, speed: 2.1, color: K.dust });
    this.debris(pos, 8, { speed: 3.2, size: 0.1, color: K.clod });
    this.sparks(up, 5, { color: [1, 0.85, 0.6], speed: 3, up: 1.4, gravity: 9, life: 0.3, size: 0.1, floor: pos.y + 0.02 });
    this.decal(pos, { type: 'dust', size: 2.0, life: 0.6, color: K.dust, alpha: 0.9 });
    this.decal(pos, { type: 'scorch', size: 0.85, life: 2.2, hold: 1.2, color: [0.1, 0.085, 0.07], bright: 0 });
    this.light(_b.copy(pos).setY(pos.y + 0.35), K.core, 1.6, 2.2, 0.12);
    this.kick(0.12);
  }
  /**
   * The cannon's muzzle blast (black powder): a white-hot flash, a jet of grey smoke thrown OUT OF THE BARREL toward
   * the target that slows and billows (the old puffs rose straight up in place, so the shot read as a chimney
   * puff), a few sparks spat forward, and a slow haze that lingers at the mouth. `target` only gives the direction.
   */
  cannonBlast(pos, target) {
    const K = PALETTE.kinetic;
    const dir = target ? _e.set(target.x - pos.x, 0, target.z - pos.z) : _e.set(0, 0, 0);
    if (dir.lengthSq() > 1e-6) dir.normalize();
    this.flash(pos, 0.55, K.core);
    this.spawn({ pos: _a.copy(pos).addScaledVector(dir, 0.12), frame: 'glow', life: 0.08, size0: 0.5, size1: 0.2, aspect: 0.5, rot: Math.atan2(dir.x, dir.z),
                 color0: K.core, bright: 2.4, fadeIn: 0, fadeOut: 1 });
    this.burst(4, (i) => ({ pos: _c.copy(pos).addScaledVector(dir, 0.05 + 0.06 * i), anim: 'smoke', additive: false,
      vel: _b.copy(dir).multiplyScalar(rnd(1.6, 2.8) * (1 - i * 0.12)).add(_d.set(rnd(-0.25, 0.25), rnd(0.15, 0.45), rnd(-0.25, 0.25))), drag: 3.2,
      life: rnd(0.7, 1.0), size0: 0.18, size1: 0.6, sizeEase: ease.out, color0: [0.5, 0.48, 0.46], alpha: 0.75, fadeIn: 0.03, fadeOut: 0.6, rot: rnd(0, 6.3), rotVel: rnd(-1, 1) }));
    this.smoke(pos, 2, { size: 0.35, life: 1.2, rise: 0.3, spread: 0.15, color: [0.58, 0.56, 0.54], alpha: 0.5 });
    this.burst(6, () => ({ pos, frame: 'spark', vel: _d.copy(dir).multiplyScalar(rnd(2.5, 4)).add(_b.set(rnd(-0.8, 0.8), rnd(0.2, 1.0), rnd(-0.8, 0.8))), gravity: 6, drag: 1.2,
      life: rnd(0.2, 0.4), size0: 0.12, size1: 0.04, aspect: 0.35, stretch: 0.25, color0: [1, 1, 1], color1: [1, 0.85, 0.55], bright: 2.0, fadeIn: 0, fadeOut: 0.5 }));
  }
  mortarFire(muzzle, target) {
    this.flash(muzzle, 0.7, [1, 0.75, 0.4]);
    this.smoke(muzzle, 4, { size: 0.45, life: 1.2, rise: 1.0, spread: 0.5 });
    this.sparks(muzzle, 8, { color: [1, 0.7, 0.3], speed: 2.5, up: 2, gravity: 5, life: 0.4 });
    this.kick(0.06);
    const pr = this.projectile({ from: muzzle, to: target, life: 1.05, frame: 'debris', size: 0.22, color: [0.45, 0.42, 0.5], arc: 1.6, trail: false, spin: 12, onHit: p => this.mortarImpact(p) });
    const tick = pr.tick; pr.tick = dt => { tick(dt); if (Math.random() < 0.7) this.spawn({ pos: pr.pos, anim: 'smoke', life: 0.9, size0: 0.14, size1: 0.45, color0: [0.95, 0.95, 1], alpha: 0.6, additive: false, fadeIn: 0, fadeOut: 0.7, vel: _b.set(0, 0.3, 0), rot: rnd(0, 6.3) }); this.spawn({ pos: pr.pos, frame: 'glow', life: 0.2, size0: 0.3, size1: 0, color0: [1, 0.6, 0.25], bright: 1.5, fadeIn: 0, fadeOut: 1 }); };
  }
  mortarImpact(pos, { color = [1, 0.8, 0.5], trauma = 0.25 } = {}) {
    this.spawn({ pos: _a.copy(pos).setY(pos.y + 0.45), anim: 'explosion', life: 0.7, size0: 1.2, size1: 2.2, sizeEase: ease.out, fadeIn: 0, fadeOut: 0.3, color0: [1, 1, 1], bright: 1.15, rot: rnd(0, 6.3) });
    this.spawn({ pos: _a.copy(pos).add(_d.set(0.2, 0.3, -0.1)), anim: 'explosion', ramp: 'dirt', additive: false, life: 0.55, size0: 0.7, size1: 1.4, sizeEase: ease.out, fadeIn: 0.1, fadeOut: 0.3, color0: [1, 1, 1], bright: 1.0, rot: rnd(0, 6.3) });
    this.flash(_a.copy(pos).setY(pos.y + 0.3), 1.1, color);
    this.ring(pos, { size: 3.4, life: 0.55, color: [1, 0.85, 0.6] });
    this.sparks(_a.copy(pos).setY(pos.y + 0.3), 22, { color: [1, 0.7, 0.3], speed: 5, up: 3, gravity: 10, life: 0.7, size: 0.2, floor: pos.y + 0.02 });
    this.debris(pos, 10, { speed: 3.5, size: 0.14, color: PALETTE.kinetic.clod });
    this.dust(pos, 10, { size: 0.7, speed: 2.2, color: PALETTE.kinetic.dust });
    this.smoke(pos, 7, { size: 0.8, life: 1.8, rise: 1.2, spread: 0.7, color: [0.5, 0.47, 0.46] });
    this.decal(pos, { type: 'scorch', size: 1.7, life: 3.9, hold: 2.4, color: [0.08, 0.065, 0.06], bright: 1 });
    this.light(_b.copy(pos).setY(pos.y + 0.5), color, 2.5, 3, 0.2);
    this.kick(trauma);
  }
  /**
   * Charge on the crystal (sparks converge, a glow swells), then after `delay` s — the tower clip's release — the arc
   * leaves the crystal and jumps target to target, each hop `ELEMENT.lightning.hopDelay` after the last, each link
   * keeping a thin residual arc for `afterglow` s. `scale` (tier) widens the arcs; the hits stay local.
   */
  teslaFire(crystal, targets, { delay = 0.22, scale = 1 } = {}) {
    const A = PALETTE.arcane, L = ELEMENT.lightning;
    // charge: sparks converge, glow swells, then arcs
    this.burst(14, () => { const dir = _d.set(rnd(-1, 1), rnd(-1, 1), rnd(-1, 1)).normalize(); return { pos: _c.copy(crystal).addScaledVector(dir, 0.55), frame: 'spark', vel: dir.multiplyScalar(-0.55 / Math.max(0.12, delay)), life: Math.max(0.12, delay), size0: 0.12, size1: 0.05, aspect: 0.35, stretch: 0.2, color0: A.rim, bright: 2, fadeIn: 0, fadeOut: 0.3 }; });
    this.spawn({ pos: crystal, frame: 'glow', life: delay + 0.08, size0: 0.1, size1: 0.6 * scale, sizeEase: ease.in, color0: A.rim, bright: 1.6, fadeIn: 0.8, fadeOut: 0.15 });
    const from = crystal.clone(), hits = targets.map(t => t.clone().add(V3(0, 0.2, 0)));
    this.later(delay, () => {
      this.spawn({ pos: from, frame: 'glow', life: 0.14, size0: 0.5 * scale, size1: 0.2, color0: A.core, color1: A.rim, bright: 1.8, fadeIn: 0, fadeOut: 1 });
      let prev = from;
      hits.forEach((hit, i) => {
        const a = prev;
        const link = () => {
          this.lightning(a, hit, { life: 0.26, width: 0.15 * scale });
          this.lightning(a, hit, { life: L.afterglow + 0.26, width: 0.045 * scale, glow: 0.12 * scale, core: 0.015, coreBright: 1.6, jag: 0.7, ambient: true });
          this.teslaImpact(hit, scale);
        };
        if (i === 0) link(); else this.later(i * L.hopDelay, link);
        prev = hit;
      });
    });
  }
  teslaImpact(pos, scale = 1) {
    const A = PALETTE.arcane;
    this.flash(pos, 0.34 * scale, A.rim);
    this.spawn({ pos, frame: 'star', life: 0.22, size0: 0.55 * scale, size1: 0.15, color0: [0.9, 0.85, 1], bright: 2.0, fadeIn: 0, fadeOut: 0.6, rot: rnd(0, 6.3) });
    this.ring(_a.copy(pos).setY(pos.y - 0.18), { size: 0.7 * scale, life: 0.28, color: A.rim, bright: 1.6 });
    this.sparks(pos, 7, { color: A.rim, speed: 2.4, up: 1.5, gravity: 6, life: 0.35, size: 0.12 });
    this.light(pos, [0.85, 0.75, 1], 1.5, 2.5, 0.15, { flicker: true });
    // stray mini-arcs
    for (let i = 0; i < 2; i++) this.lightning(pos, _a.copy(pos).add(_d.set(rnd(-0.5, 0.5), rnd(-0.3, 0.3), rnd(-0.5, 0.5))), { life: 0.12, width: 0.07, ambient: true });
  }
  railgunFire(muzzle, target) {
    this.burst(10, () => { const d = _d.set(rnd(-1, 1), rnd(-1, 1), rnd(-1, 1)).normalize(); return { pos: _c.copy(muzzle).addScaledVector(d, 0.4), frame: 'spark', vel: d.multiplyScalar(-1.8), life: 0.22, size0: 0.1, size1: 0.04, aspect: 0.35, stretch: 0.2, color0: [0.6, 0.9, 1], bright: 2, fadeIn: 0, fadeOut: 0.3 }; });
    this.glowPulse(muzzle, { size: 0.5, life: 0.25, color: [0.6, 0.9, 1] });
    const m = muzzle.clone(), tg = target.clone();
    this.later(0.2, () => {
      this.flash(m, 0.7, [0.7, 0.95, 1]);
      this.beam(m, tg, { life: 0.22, width: 0.22 });
      this.spawn({ pos: _a.copy(m).lerp(tg, 0.5), frame: 'spark', life: 0.16, size0: m.distanceTo(tg) * 1.1, size1: m.distanceTo(tg) * 0.9, aspect: 0.06, color0: [1, 1, 1], bright: 2.5, fadeIn: 0, fadeOut: 0.8, stretch: 0, rot: 0 });
      this.sparks(_a.copy(tg).setY(tg.y + 0.2), 12, { color: [0.6, 0.9, 1], speed: 3.5, up: 2, gravity: 8, life: 0.45, floor: tg.y + 0.02 });
      this.flash(_a.copy(tg).setY(tg.y + 0.2), 0.7, [0.7, 0.95, 1]);
      this.ring(tg, { size: 1.0, life: 0.28, color: [0.6, 0.9, 1] });
      this.dust(tg, 6, { size: 0.4, speed: 1.4 });
    });
  }
  meleeHit(pos, facing = 0, color = [1, 0.95, 0.8]) {
    const at = _a.copy(pos).setY(pos.y + 0.15);
    this.spawn({ pos: at, frame: 'slash', life: 0.2, size0: 0.4, size1: 0.75, sizeEase: ease.out, color0: color, bright: 2.0, fadeIn: 0, fadeOut: 0.5, rot: facing + rnd(-0.3, 0.3) });
    this.spawn({ pos: at, frame: 'star', life: 0.15, size0: 0.35, size1: 0.1, color0: [1, 1, 1], bright: 2.2, fadeIn: 0, fadeOut: 0.7, rot: rnd(0, 6.3) });
    this.sparks(at, 5, { color: [1, 0.85, 0.4], speed: 2, up: 1.5, gravity: 6, life: 0.3, size: 0.09 });
  }
  unitDeath(pos, { coins = 4 } = {}) {
    this.smoke(pos, 3, { size: 0.35, life: 0.8, rise: 0.8, spread: 0.3, color: [0.85, 0.85, 0.95] });
    this.spawn({ pos: _a.copy(pos).setY(pos.y + 0.15), frame: 'glow', life: 0.25, size0: 0.5, size1: 0.9, color0: [1, 1, 1], bright: 1.4, fadeIn: 0, fadeOut: 0.8 });
    this.sparkles(pos, 6, { radius: 0.2, rise: 1.0, life: 0.7, size: 0.14 });
    const at = _c.copy(pos).setY(pos.y + 0.1);
    for (let i = 0; i < coins; i++) this.spawn({ pos: at, frame: 'coin', anim: this.anims.coinspin ? 'coinspin' : null, animRate: rnd(14, 20), vel: _b.set(rnd(-1, 1), rnd(2.5, 3.5), rnd(-1, 1)), gravity: 9, drag: 0.2, life: rnd(0.9, 1.2), size0: 0.16, size1: 0.16, additive: false, color0: [1, 1, 1], fadeIn: 0, fadeOut: 0.2, rotVel: rnd(-8, 8), floor: pos.y + 0.08 });
  }
  // ---------------------------------------------------------------- v6: real projectiles
  // The client spawns the `proj_*` GLB and moves it; these three hooks dress the shot.
  //   const p = spawnModel('proj_arrow'); vfx.muzzle('archer', muzzlePos, targetPos);
  //   while it flies, once per `TRAIL_SPACING[kind]` units travelled:  vfx.trail('archer', p.position, p.velocity);
  //   on arrival:                 vfx.impact('archer', hitPos);
  // `towerFire` is the one-call version for towers whose shot is instantaneous (lightning, beacon).
  muzzle(kind, pos, target, scale = 1) {
    const prev = this.prio; this.prio = PRIO.muzzle;
    const at = pos;
    const yaw = target ? Math.atan2(target.x - pos.x, target.z - pos.z) : 0;
    const F = PALETTE.fire, K = PALETTE.kinetic;
    switch (kind) {
      case 'archer': case 'ballista': {                     // bowstring snap: a thin puff and a couple of chips
        this.spawn({ pos: at, frame: 'spark', life: 0.10, size0: 0.5, size1: 0.1, aspect: 0.35, rot: yaw,
                     color0: [1, 0.98, 0.85], bright: 2.6, fadeIn: 0, fadeOut: 1, stretch: 0.3 });
        this.dust(at, 2, { size: 0.22, speed: 0.8 });
        this.sparks(at, kind === 'ballista' ? 5 : 3, { color: [1, 0.9, 0.6], speed: 1.6, up: 0.6, gravity: 5, life: 0.22, size: 0.08 });
        break;
      }
      case 'cannon': this.cannonBlast(at, target); break;   // black-powder: white-hot flash, dark smoke, no orange ball
      case 'mortar':
        this.flash(at, 0.75, [1, 0.75, 0.4]);
        this.smoke(at, 5, { size: 0.5, life: 1.3, rise: 1.1, spread: 0.5 });
        this.ring(at, { size: 0.9, life: 0.3, color: [1, 0.8, 0.4], flat: false });
        this.kick(0.06); break;
      case 'frost': {                                       // the shard leaves: a cold puff and glints, no bloom flash
        const R = PALETTE.frost;
        this.spawn({ pos: at, frame: 'glow', life: 0.14, size0: 0.3 * scale, size1: 0.1 * scale, color0: R.rim, bright: 1.2, fadeIn: 0, fadeOut: 1 });
        this.smoke(at, 2, { size: 0.26 * scale, life: 0.55, rise: 0.12, spread: 0.3, color: ELEMENT.frost.mist, alpha: 0.4 });
        this.sparkles(at, 4, { color: R.mote, radius: 0.12 * scale, rise: 0.3, life: 0.4, size: 0.09 * scale }); break;
      }
      case 'fire': {                                        // the lob: a hot gout out of the nozzle toward the target, sparks, soot
        this.flash(at, 0.26 * scale, F.rim);
        const aim = target ? _f.subVectors(target, at).setY(0) : _f.set(0, 0, 0);
        if (aim.lengthSq() > 1e-6) aim.normalize();
        // three tongues thrown along the aim (tip first: `align` lays the flame cell along the velocity), slowing hard
        for (let i = 0; i < 3; i++) this.spawn({ pos: at, anim: 'flame', animRate: 18, ramp: this.hot(FIRE.heat.gout), align: true, vel: _g.copy(aim).multiplyScalar(rnd(1.2, 2.2)).setY(rnd(0.3, 0.7)), drag: 4, life: rnd(0.2, 0.3), size0: 0.1 * scale, size1: 0.2 * scale, aspect: 2, rot: -Math.PI / 2, fadeIn: 0, fadeOut: 0.7, bright: 1.1 });
        this.fireSmoke(at, 2, { size: 0.28 * scale, life: 0.9, rise: 0.7 });
        this.sparks(at, 5, { color: F.rim, speed: 2, up: 1.2, gravity: 3, life: 0.4, size: 0.09 }); break;
      }
      case 'poison':                                        // the glob is spat: a green belch, drips and bubbles
        this.spawn({ pos: at, frame: 'glow', life: 0.2, size0: 0.32 * scale, size1: 0.12, color0: PALETTE.poison.core, color1: PALETTE.poison.rim, bright: 1.3, fadeIn: 0, fadeOut: 1 });
        this.sparks(at, 4, { color: PALETTE.poison.rim, speed: 1.4, up: 1.0, gravity: 5, life: 0.45, size: 0.1 });
        this.bubbles(at, 2, { color: PALETTE.poison.core, radius: 0.08, size: 0.08 }); break;
      case 'beacon':
        this.glowPulse(at, { size: 0.7, life: 0.3, color: PALETTE.holy.core });
        this.ring(at, { size: 1.0, life: 0.35, color: PALETTE.holy.rim, flat: false }); break;
      // ---- unit-scale kinds. Units are ~1/8 the height of a tower, so these are deliberately small: a
      // tower-sized puff on a 0.22-unit archer swallows the unit whole.
      case 'unit_bow':
        this.spawn({ pos: at, frame: 'spark', life: 0.08, size0: 0.18, size1: 0.03, aspect: 0.3, rot: yaw,
                     color0: [1, 0.98, 0.88], bright: 2.0, fadeIn: 0, fadeOut: 1, stretch: 0.12 });
        break;
      case 'bomb':                                          // the lob: a grunt of dust, no flash (it explodes later)
        this.dust(at, 2, { size: 0.12, speed: 0.6 });
        this.spawn({ pos: at, frame: 'glow', life: 0.18, size0: 0.12, size1: 0.02, color0: [1, 0.6, 0.25], bright: 1.6, fadeIn: 0, fadeOut: 1 });
        break;
      case 'heal':
        this.glowPulse(at, { size: 0.22, life: 0.22, color: [0.6, 1, 0.45] });
        this.sparkles(at, 4, { color: [0.75, 1, 0.6], radius: 0.1, rise: 0.3, life: 0.4, size: 0.06 }); break;
      case 'soul':
        this.glowPulse(at, { size: 0.24, life: 0.25, color: [0.72, 0.4, 1] });
        this.sparks(at, 4, { color: [0.75, 0.45, 1], speed: 0.9, up: 0.6, gravity: 1.5, life: 0.4, size: 0.06 }); break;
      case 'slug':
        this.flash(at, 0.2, [1, 0.8, 0.45]);
        this.smoke(at, 2, { size: 0.14, life: 0.45, rise: 0.3, spread: 0.15 });
        this.sparks(at, 4, { color: [1, 0.8, 0.4], speed: 1.4, up: 0.5, gravity: 5, life: 0.22, size: 0.05 }); break;
      case 'plague':
        this.glowPulse(at, { size: 0.22, life: 0.25, color: [0.6, 1, 0.35] });
        this.sparks(at, 3, { color: [0.6, 1, 0.35], speed: 0.8, up: 0.6, gravity: 3, life: 0.4, size: 0.06 }); break;
      default:
        this.flash(at, 0.4, [1, 0.9, 0.7]);
    }
    this.prio = prev;
  }

  /**
   * One trail emission for a projectile in flight. The caller emits by DISTANCE, once every `VFX.TRAIL_SPACING[kind]`
   * units (vfx.md F4), so the density no longer doubles at 120 Hz or gaps at 30. `vel` points the streaks.
   */
  trail(kind, pos, vel) {
    const prev = this.prio; this.prio = PRIO.trail;
    const v = vel ? _b.copy(vel).multiplyScalar(0.1) : _b.set(0, 0, 0);
    switch (kind) {
      case 'archer': case 'ballista':
        this.spawn({ pos, frame: 'spark', life: 0.09, size0: 0.34, size1: 0.02, aspect: 0.28,
                     color0: [1, 1, 0.95], bright: 1.5, fadeIn: 0, fadeOut: 1, stretch: 0.16, vel: v });
        break;
      case 'cannon': case 'mortar':                         // grey powder smoke — the atlas has no `smoke` FRAME, only the anim
        this.spawn({ pos, anim: 'smoke', additive: false, life: 0.55, size0: 0.2, size1: 0.5, color0: [0.62, 0.6, 0.58], alpha: 0.55,
                     fadeIn: 0.1, fadeOut: 0.8, vel: _d.set(0, 0.25, 0), rot: rnd(0, 6.3), rotVel: rnd(-1, 1) });
        break;
      case 'fire':
        // hot wisps shed off the lob, cooling to red as they fade (the hot ramp), where a pale glow streak used to be
        { const k = rnd(0.7, 1.3); this.spawn({ pos: _c.copy(pos).add(_d.set(rnd(-0.03, 0.03), rnd(-0.03, 0.03), rnd(-0.03, 0.03))), anim: 'flame', animRate: 20, ramp: this.hot(FIRE.heat.wisp), life: 0.26 * k, size0: 0.1 * k, size1: 0.04, aspect: 2, rot: rnd(-0.4, 0.4), vel: _d.set(rnd(-0.12, 0.12), rnd(0.25, 0.45), rnd(-0.12, 0.12)), fadeIn: 0, fadeOut: 0.9, bright: 0.8 }); }
        // embers shed off the lob, falling behind it
        if (Math.random() < 0.45) this.spawn({ pos, frame: 'glow', life: 0.45, size0: 0.07, size1: 0.02, vel: _d.set(rnd(-0.3, 0.3), rnd(0, 0.4), rnd(-0.3, 0.3)), gravity: 2.5, color0: PALETTE.fire.core, color1: PALETTE.fire.rim, bright: 2.4, fadeIn: 0, fadeOut: 0.6 });
        if (Math.random() < 0.35) this.spawn({ pos, anim: 'smoke', additive: false, life: 0.8, size0: 0.1, size1: 0.38, sizeEase: ease.out, color0: FIRE.soot, alpha: FIRE.sootAlpha * 0.8,
                     fadeIn: FIRE.sootIn, fadeOut: FIRE.sootOut, vel: _d.set(0, 0.35, 0), rot: rnd(0, 6.3) });
        break;
      case 'frost':                                         // a cold streak, a mist wake and glints hanging in the air
        this.spawn({ pos, frame: 'glow', life: 0.22, size0: 0.13, size1: 0.02, color0: PALETTE.frost.rim, bright: 0.9, fadeIn: 0, fadeOut: 1 });
        if (Math.random() < 0.5) this.spawn({ pos, anim: 'smoke', additive: false, life: 0.55, size0: 0.08, size1: 0.26, color0: ELEMENT.frost.mist, alpha: 0.35, fadeIn: 0.1, fadeOut: 0.8, vel: _d.set(0, -0.05, 0), rot: rnd(0, 6.3) });
        if (Math.random() < 0.4) this.sparkles(pos, 1, { color: PALETTE.frost.mote, radius: 0.06, rise: 0.1, life: 0.45, size: 0.08 });
        break;
      case 'poison':
        this.spawn({ pos, frame: 'glow', life: 0.3, size0: 0.18, size1: 0.04, color0: PALETTE.poison.core, color1: PALETTE.poison.rim, bright: 1.1, fadeIn: 0, fadeOut: 1, vel: _d.set(0, -0.2, 0) });
        // drips off the glob
        if (Math.random() < 0.3) this.spawn({ pos, frame: 'glow', life: 0.5, size0: 0.06, size1: 0.04, vel: _d.set(rnd(-0.1, 0.1), -0.2, rnd(-0.1, 0.1)), gravity: 4, color0: PALETTE.poison.core, color1: PALETTE.poison.rim, bright: 1.4, fadeIn: 0, fadeOut: 0.4, floor: pos.y - 0.6 });
        break;
      case 'beacon':
        this.spawn({ pos, frame: 'glow', life: 0.3, size0: 0.3, size1: 0.05, color0: PALETTE.holy.core, bright: 2.0, fadeIn: 0, fadeOut: 1 });
        break;
      case 'unit_bow':
        this.spawn({ pos, frame: 'spark', life: 0.07, size0: 0.14, size1: 0.01, aspect: 0.26,
                     color0: [1, 1, 0.95], bright: 1.2, fadeIn: 0, fadeOut: 1, stretch: 0.08, vel: v });
        break;
      case 'bomb':                                          // a lit fuse trailing smoke and sparks on the arc
        this.spawn({ pos, anim: 'smoke', additive: false, life: 0.4, size0: 0.07, size1: 0.2, color0: [0.6, 0.58, 0.56], alpha: 0.6, fadeIn: 0.1, fadeOut: 0.8, vel: _d.set(0, 0.18, 0) });
        if (Math.random() < 0.5) this.sparks(pos, 1, { color: [1, 0.7, 0.3], speed: 0.5, up: 0.3, gravity: 2, life: 0.25, size: 0.04 });
        break;
      case 'heal':
        this.spawn({ pos, frame: 'glow', life: 0.24, size0: 0.13, size1: 0.01, color0: [0.65, 1, 0.5], bright: 1.6, fadeIn: 0, fadeOut: 1 });
        break;
      case 'soul':
        this.spawn({ pos, frame: 'glow', life: 0.3, size0: 0.15, size1: 0.02, color0: [0.72, 0.4, 1], bright: 1.8, fadeIn: 0, fadeOut: 1, vel: _d.set(0, 0.1, 0) });
        break;
      case 'slug':
        this.spawn({ pos, frame: 'spark', life: 0.1, size0: 0.16, size1: 0.02, aspect: 0.3, color0: [1, 0.8, 0.45], bright: 1.8, fadeIn: 0, fadeOut: 1, stretch: 0.1, vel: v });
        break;
      case 'plague':
        this.spawn({ pos, frame: 'glow', life: 0.28, size0: 0.12, size1: 0.02, color0: [0.55, 1, 0.35], bright: 1.3, fadeIn: 0, fadeOut: 1, vel: _d.set(0, -0.12, 0) });
        break;
      default:
        this.spawn({ pos, frame: 'glow', life: 0.25, size0: 0.22, size1: 0.02, color0: [1, 1, 1], bright: 1.2, fadeIn: 0, fadeOut: 1 });
    }
    this.prio = prev;
  }

  impact(kind, pos, facing = 0, scale = 1) {
    const prev = this.prio; this.prio = PRIO.impact;
    const at = pos.clone();
    const F = PALETTE.fire, R = PALETTE.frost, Pn = PALETTE.poison, H = PALETTE.holy;
    switch (kind) {
      case 'archer': case 'ballista': {                     // a hit you feel: chips, a puff, a hard white tick
        this.flash(at, kind === 'ballista' ? 0.5 : 0.34, PALETTE.kinetic.core);
        this.debris(at, kind === 'ballista' ? 6 : 3, { speed: 2.6, size: 0.08, color: PALETTE.kinetic.clod });
        this.dust(at, 3, { size: 0.3, speed: 1.4, color: PALETTE.kinetic.dust });
        this.sparks(at, kind === 'ballista' ? 10 : 6, { color: [1, 0.9, 0.7], speed: 3, up: 1.6, gravity: 8, life: 0.35, size: 0.1 });
        break;
      }
      case 'cannon': this.cannonHit(at); break;
      case 'mortar': this.mortarImpact(at); break;
      case 'frost': {                                       // the shard shatters ON the enemy: ice shards, cold mist, a
        const s = scale;                                    // small frost star — one unit's size (frost has no area)
        this.spawn({ pos: _a.copy(at).setY(at.y + 0.12), frame: 'glow', life: 0.22, size0: 0.2 * s, size1: 0.6 * s, color0: R.core, color1: R.rim, bright: 1.0, fadeIn: 0, fadeOut: 0.8 });
        this.spawn({ pos: _a, frame: 'star', life: 0.2, size0: 0.45 * s, size1: 0.12 * s, color0: R.core, color1: R.rim, bright: 1.2, fadeIn: 0, fadeOut: 0.7, rot: rnd(0, 6.3) });
        this.debris(_a, Math.round(8 * s), { speed: 2.1, size: 0.08 * s, color: [0.85, 0.95, 1.1], kind: 'ice' });
        this.smoke(at, 3, { size: 0.42 * s, life: 0.85, rise: 0.2, spread: 0.4, color: ELEMENT.frost.mist, alpha: 0.5 });
        this.sparkles(at, 5, { color: R.mote, radius: 0.22 * s, rise: 0.35, life: 0.7, size: 0.11 });
        this.ring(at, { size: 0.75 * s, life: 0.3, color: R.rim, bright: 1.4 });
        this.decal(at, { type: 'frost', size: 0.75 * s, life: 1.8, hold: 0.7, color: R.rim, bright: 0.9 });
        break;
      }
      case 'fire': {                                        // a fireball bursts on its one target and sets it alight
        const s = scale;
        this.spawn({ pos: _a.copy(at).setY(at.y + 0.2), anim: 'explosion', ramp: this.hot(FIRE.heat.burst), life: 0.42, size0: 0.35 * s, size1: 0.85 * s, sizeEase: ease.out, fadeIn: 0, fadeOut: 0.6, color0: this.frameRamp.explosion_00 >= 0 ? [1, 1, 1] : F.core, bright: 1, rot: rnd(0, 6.3) });
        // two tongues lick up off the target after the burst
        this.flames(at, { life: 0.55, size: 0.36 * s });
        this.flames(_d.copy(at).add(_b.set(rnd(-0.1, 0.1), 0, rnd(-0.1, 0.1))), { life: 0.45, size: 0.26 * s });
        this.ring(at, { size: 0.85 * s, life: 0.3, color: F.rim, bright: 1.2 });
        this.fireSmoke(at, 3, { size: 0.42 * s, life: 1.3, rise: 0.8 });
        this.sparks(at, 6, { color: F.rim, speed: 2.6, up: 2.0, gravity: 7, life: 0.5, size: 0.11 });
        this.decal(at, { type: 'scorch', size: 0.75 * s, life: 2.6, hold: 1.2, color: [0.09, 0.07, 0.06], bright: 1.1 });
        this.light(_b.copy(at).setY(at.y + 0.35), F.rim, 1.0, 1.3, 0.18);
        this.later(0.2, () => this.embers(at, 3, { radius: 0.1 }));
        break;
      }
      case 'poison': {                                      // the glob splats: green spatter, a small bubbling puddle
        const s = scale;
        this.spawn({ pos: _a.copy(at).setY(at.y + 0.1), frame: 'glow', life: 0.4, size0: 0.26 * s, size1: 0.7 * s, color0: Pn.core, color1: Pn.rim, bright: 0.9, fadeIn: 0, fadeOut: 0.7 });
        this.burst(Math.round(9 * s), () => ({ pos: _a, frame: 'glow', vel: _d.set(rnd(-1, 1), rnd(0.6, 1.4), rnd(-1, 1)).multiplyScalar(1.6), gravity: 9, drag: 0.4, life: rnd(0.35, 0.6), size0: 0.09, size1: 0.05, color0: Pn.core, color1: Pn.rim, bright: 1.4, fadeIn: 0, fadeOut: 0.3, floor: at.y - 0.1 }));
        this.ring(at, { size: 0.8 * s, life: 0.45, color: Pn.rim, bright: 1.3 });
        this.smoke(at, 2, { size: 0.36 * s, life: 1.2, rise: 0.35, color: Pn.smoke, alpha: 0.6 });
        this.bubbles(at, 4, { color: Pn.core, radius: 0.18 * s });
        this.decal(at, { type: 'poison', size: 0.8 * s, life: 2.2, hold: 1.0, color: Pn.rim, bright: 0.8 });
        break;
      }
      case 'beacon':
        this.flash(at, 0.8, H.core);
        this.ring(at, { size: 1.8, life: 0.45, color: H.rim });
        this.sparkles(at, 10, { color: H.rim, radius: 0.4, rise: 1.2, life: 0.8, size: 0.15 });
        break;
      case 'unit_bow':
        this.flash(at, 0.16, [1, 0.97, 0.85]);
        this.sparks(at, 3, { color: [1, 0.9, 0.7], speed: 1.4, up: 0.7, gravity: 8, life: 0.22, size: 0.05 });
        this.dust(at, 1, { size: 0.12, speed: 0.6 });
        break;
      case 'bomb':                                          // the payoff: this one is allowed to be loud
        this.spawn({ pos: _a.copy(at).setY(at.y + 0.1), anim: 'explosion', life: 0.45, size0: 0.35, size1: 0.95, fadeIn: 0, fadeOut: 0.4, color0: this.frameRamp.explosion_00 >= 0 ? [1, 1, 1] : F.core, bright: 1.4 });
        this.ring(at, { size: 0.9, life: 0.3, color: F.rim });
        this.smoke(at, 3, { size: 0.3, life: 0.9, rise: 0.8, color: F.smoke });
        this.sparks(at, 8, { color: [1, 0.7, 0.3], speed: 2.4, up: 1.6, gravity: 7, life: 0.45, size: 0.09 });
        this.debris(at, 4, { speed: 1.8, size: 0.05, color: PALETTE.kinetic.clod });
        this.kick(0.08);
        break;
      case 'heal':                                          // lands on a friendly: a soft bloom, no debris
        this.glowPulse(_a.copy(at).setY(at.y + 0.1), { size: 0.45, life: 0.35, color: [0.7, 1, 0.55] });
        this.ring(at, { size: 0.5, life: 0.4, color: [0.7, 1, 0.55] });
        this.sparkles(at, 6, { color: [0.8, 1, 0.65], radius: 0.15, rise: 0.6, life: 0.6, size: 0.07 });
        break;
      case 'soul':
        this.flash(at, 0.28, [0.8, 0.5, 1]);
        this.ring(at, { size: 0.6, life: 0.35, color: [0.72, 0.4, 1] });
        this.sparks(at, 6, { color: [0.78, 0.5, 1], speed: 1.6, up: 1.0, gravity: 3, life: 0.5, size: 0.07 });
        break;
      case 'slug':
        this.flash(at, 0.24, [1, 0.85, 0.5]);
        this.sparks(at, 6, { color: [1, 0.8, 0.4], speed: 2.2, up: 1.0, gravity: 8, life: 0.3, size: 0.06 });
        this.debris(at, 2, { speed: 1.6, size: 0.05 });
        break;
      case 'plague':
        this.spawn({ pos: _a.copy(at).setY(at.y + 0.06), frame: 'glow', life: 0.6, size0: 0.24, size1: 0.8, color0: [0.55, 1, 0.35], bright: 1.1, fadeIn: 0.05, fadeOut: 0.6 });
        this.ring(at, { size: 0.7, life: 0.5, color: [0.6, 1, 0.4] });
        this.smoke(at, 3, { size: 0.26, life: 1.1, rise: 0.3, color: [0.45, 0.75, 0.35] });
        break;
      default: this.meleeHit(at, facing);
    }
    this.prio = prev;
  }

  // ---------------------------------------------------------------- the fortress (Phase 2: "The Fortress Strike")
  // baseCharge() when the base starts its 1.5 s `charge` clip; baseStrike() on the sim's TowerFired. The strike is
  // staged: a 100 ms leader, then the bolt (sky → conductor → target, violet-white, three layers, a restrike and an
  // afterglow), the impact (a pillar of light at the target, scorch + crack, a light slot) and the aftermath (embers,
  // a lit smoke column, residual arcs). `onStrike(at)` runs on the frame the main bolt lands, for the client's beats
  // that live outside this module: hit-stop, StrikeLight, exposure punch, screen flash.
  //
  // Readability from the default phone camera: the part of the strike that is always on screen is the fortress
  // itself (the camera opens on it), so the sky bolt into the conductor and the conductor flash carry the moment even
  // when the target is at the bottom edge or under the card hand; the target gets a 3-unit pillar that climbs up the
  // screen; the bolts are `xray` (drawn over the keep and trees). The client adds the screen flash + exposure punch.
  baseCharge(tip, { keep = null, seconds = 1.5 } = {}) {
    const prev = this.prio; this.prio = PRIO.hero;
    const at = tip.clone(), A = this.strikePalette ?? PALETTE.arcane;
    // The charge's pending beats and its light, so a strike that lands before the charge has run (the fortress fires
    // as soon as something steps in range) ends it: no motes still spiralling in after the bolt has gone.
    this._endCharge();
    const charge = this._charge = { timers: [], light: null };
    const later = (t, fn) => { const tm = { t, fn }; this.timers.push(tm); charge.timers.push(tm); };
    // swelling glow at the conductor (sprites, round — no upright shock rings: their last frame drew a square)
    for (let k = 0; k < 4; k++) later(k * seconds * 0.24, () => this.glowPulse(at, { size: 0.5 + k * 0.3, life: 0.4, color: A.rim }));
    // motes spiral IN to the conductor: inward at 2.2 u/s with a 1.4 u/s swirl about the vertical, living just long
    // enough to arrive (Phase 1 fixed their straight-up drift; Phase 2 makes it a visible vortex)
    const n = 20;
    for (let k = 0; k < n; k++) {
      later(k * (seconds * 0.85) / n, () => {
        const ang = rnd(0, 6.283), rad = rnd(0.8, 1.6);
        const off = _d.set(Math.cos(ang) * rad, rnd(-0.9, 0.1), Math.sin(ang) * rad), dist = Math.max(0.3, off.length());
        const p = _c.copy(at).add(off);
        const vel = _b.copy(off).multiplyScalar(-2.2 / dist).add(_a.set(off.z, 0, -off.x).normalize().multiplyScalar(1.4));
        this.spawn({ pos: p, frame: 'spark', life: Math.min(0.7, dist / 2.2), size0: 0.18, size1: 0.05, aspect: 0.4, stretch: 0.12,
                     color0: A.core, color1: A.rim, bright: 2.6, fadeIn: 0.12, fadeOut: 0.3, vel });
      });
    }
    for (let k = 0; k < 5; k++) {                       // little arcs licking the coils, quickening
      later(seconds * (0.3 + k * 0.13), () => this.lightning(_a.copy(at).add(_d.set(rnd(-0.35, 0.35), rnd(-0.45, 0.05), rnd(-0.35, 0.35))), at, { life: 0.12, width: 0.06 }));
    }
    // a pulsing violet light slot that builds over the charge and cuts when the bolt takes over
    charge.light = this.light(at, A.rim, 3, 2.6, seconds, { rise: true, pulse: 3 });
    // the rune circle under the keep, turning, fading in over the charge
    if (keep) this.decal(keep, { type: 'rune', size: 3.6, life: seconds + 0.7, hold: seconds + 0.1, fadeIn: seconds * 0.6, spin: 0.7, color: A.rim, bright: 1.2, rot: 0 });   // at 1.8 the violet burned to white
    this.prio = prev;
  }

  /** Drops a running charge's pending beats and cuts its light (a no-op once the charge has run its course). */
  _endCharge() {
    const c = this._charge; this._charge = null;
    if (!c) return;
    const T = this.timers; let w = 0;
    for (let i = 0; i < T.length; i++) if (!c.timers.includes(T[i])) T[w++] = T[i];
    T.length = w;
    const l = c.light;                                  // still ours only while it is the rising charge light
    if (l && l.on && l.rise) { l.on = false; l.k = 0; }
  }

  baseStrike(tip, target, { onStrike = null, leader = 0.1 } = {}) {
    const prev = this.prio; this.prio = PRIO.hero;
    this._endCharge();
    const from = tip.clone(), to = target.clone();
    this.strikeLeader(from, to);
    this.later(leader, () => {
      const p0 = this.prio; this.prio = PRIO.hero;
      this.strikeBolt(from, to);
      this.strikeImpact(to);
      this.strikeAftermath(to);
      this.prio = p0;
      onStrike?.(to);
    });
    this.prio = prev;
  }
  /** The 100 ms leader that finds the path: thin, dim, extra jagged. */
  strikeLeader(from, to) {
    return this.lightning(from, to, { life: 0.1, width: 0.05, glow: 0.18, core: 0.02, coreBright: 1.4, jag: 1.4, xray: true, color: (this.strikePalette ?? PALETTE.arcane).hex });
  }
  /** The bolt: violet-white, glow 0.9 / mid 0.3 / core 0.08 at 3× on one path; a sky bolt into the conductor, forks, a restrike, an afterglow. */
  strikeBolt(from, to) {
    const A = this.strikePalette ?? PALETTE.arcane;
    this.flash(from, 1.8, [0.92, 0.86, 1]);
    this.lightning(_a.copy(from).add(_d.set(rnd(-0.4, 0.4), 2.8, rnd(-0.4, 0.4))), from, { life: 0.3, width: 0.24, glow: 0.8, core: 0.07, coreBright: 3, color: A.hex, xray: true });
    this.lightning(from, to, { life: 0.42, width: 0.30, glow: 0.9, core: 0.08, coreBright: 3, color: A.hex, xray: true });
    for (let k = 0; k < 4; k++) {                       // branches forking off the main bolt
      const mid = from.clone().lerp(to, 0.35 + k * 0.15);
      const off = mid.clone().add(V3(rnd(-1.4, 1.4), rnd(-0.2, 1.0), rnd(-1.4, 1.4)));
      this.later(0.01 * k, () => this.lightning(mid, off, { life: 0.22, width: 0.12, glow: 0.35, core: 0.04, color: A.hex, xray: true }));
    }
    const f = from.clone(), t = to.clone();
    this.later(0.09, () => this.lightning(f, t, { life: 0.16, width: 0.26, glow: 0.8, core: 0.075, coreBright: 3.2, color: A.hex, xray: true }));
    this.later(0.2, () => this.lightning(f, t, { life: 0.35, width: 0.16, glow: 0.6, core: 0.03, coreBright: 1.2, jag: 0.4, color: A.hex }));
  }
  strikeImpact(to) {
    const A = this.strikePalette ?? PALETTE.arcane;
    const up = _c.copy(to); up.y += 3.2;
    this.beam(to, up, { life: 0.35, width: 0.8, color: A.coreHex });          // the pillar: climbs up the screen
    // the strike hits 3x harder since 2026-09-30 (DESIGN §7): a wider ring, a second shockwave behind it, more clods, a
    // harder kick — the same rhythm and sprites, more weight
    this.ring(to, { size: 3.6, life: 0.55, color: A.rim, bright: 2.8 });
    this.ring(to, { size: 1.6, life: 0.3, color: [1, 1, 1] });
    const shock = to.clone();
    this.later(0.07, () => this.ring(shock, { size: 2.4, life: 0.45, color: [1, 0.95, 1], bright: 1.6 }));
    this.flash(_a.copy(to).setY(to.y + 0.3), 1.6, [0.95, 0.9, 1]);
    this.sparks(to, 26, { color: A.rim, speed: 5.5, up: 3, gravity: 9, life: 0.65, size: 0.17, floor: to.y + 0.02 });
    this.debris(to, 12, { speed: 3.6, size: 0.11, color: PALETTE.kinetic.clod });
    this.decal(to, { type: 'scorch', size: 1.8, life: 5.5, hold: 3.5, color: [0.07, 0.06, 0.08], bright: 0.8 });
    this.decal(to, { type: 'crack', size: 2.6, life: 5.5, hold: 3.5, color: A.rim, bright: 1.6 });
    this.light(_b.copy(to).setY(to.y + 0.5), [0.85, 0.75, 1], 4.5, 6, 0.3, { flicker: true });
    this.kick(0.6);
  }
  /** What stays: embers off the scorch, a violet-lit smoke column, two residual arcs across the crack. */
  strikeAftermath(to) {
    const at = to.clone(), A = this.strikePalette ?? PALETTE.arcane;
    this.later(0.25, () => this.embers(at, 10, { color: A.rim, radius: 0.45 }));
    for (let k = 0; k < 7; k++) {
      this.later(0.15 + k * 0.22, () => {
        this.spawn({ pos: _c.copy(at).add(_d.set(rnd(-0.15, 0.15), 0.1, rnd(-0.15, 0.15))), anim: 'smoke', additive: false, life: 2.4, size0: 0.35, size1: 1.2, sizeEase: ease.out,
                     color0: [0.6, 0.55, 0.72], color1: [0.42, 0.4, 0.46], alpha: 0.55, fadeIn: 0.15, fadeOut: 0.5, vel: _b.set(rnd(-0.08, 0.08), 0.55, rnd(-0.08, 0.08)), rot: rnd(0, 6.3), rotVel: rnd(-0.6, 0.6) });
        this.spawn({ pos: _c.copy(at).setY(at.y + 0.15), frame: 'glow', life: 0.6, size0: 0.9, size1: 0.5, color0: A.rim, bright: 0.9, fadeIn: 0.2, fadeOut: 0.6 });  // underlight
      });
    }
    this.light(_b.copy(at).setY(at.y + 0.3), A.rim, 2, 1.3, 1.8);                 // the column is lit from its foot
    for (let k = 0; k < 2; k++) this.later(0.4 + k * 0.45, () => {
      const a = rnd(0, 6.283), b = a + rnd(1.5, 3);
      this.lightning(_a.copy(at).add(_d.set(Math.cos(a) * 0.7, 0.05, Math.sin(a) * 0.7)), _c.copy(at).add(_b.set(Math.cos(b) * 0.7, 0.05, Math.sin(b) * 0.7)), { life: 0.1, width: 0.05, ambient: true });
    });
  }

  /** A shot between strikes (BASE_ZAP): a violet-white bolt from the conductor `tip` onto `target`, the tesla hit. */
  baseZap(tip, target, { scale = 1 } = {}) {
    const prev = this.prio; this.prio = PRIO.impact;
    const Z = BASE_ZAP, A = this.strikePalette ?? PALETTE.arcane;
    this.flash(tip, Z.tipFlash * scale, [0.92, 0.86, 1]);
    this.lightning(tip, target, { life: Z.life, width: Z.width * scale, glow: Z.glow * scale, core: Z.core * scale, coreBright: Z.coreBright, color: A.hex, xray: true });
    this.teslaImpact(target, scale);
    this.prio = prev;
  }

  // ---------------------------------------------------------------- bosses (Phase 2: "Boss Arrival and Fall")
  /**
   * Boss arrival: a ground burst where it rises — a red crack, a dust skirt, rock clods thrown up, a red light that
   * swells — then a second, smaller heave 0.35 s later as it clears the ground. `size` ~ the boss's scale. Trauma comes
   * from the camera's own boss shake (IsoCamera.shake → 0.6).
   */
  bossArrival(pos, { size = 1.4 } = {}) {
    const prev = this.prio; this.prio = PRIO.hero;
    const at = pos.clone(), B = PALETTE.blood, K = PALETTE.kinetic;
    this.decal(at, { type: 'crack', size: 3.2 * size, life: 5, hold: 3, color: B.rim, bright: 1.8 });
    this.decal(at, { type: 'dust', size: 3.4 * size, life: 0.8, color: K.dust, alpha: 0.95 });
    this.ring(at, { size: 3.0 * size, life: 0.55, color: B.rim, bright: 2.2 });
    this.flash(_a.copy(at).setY(at.y + 0.3), 1.2 * size, B.core);
    this.debris(at, 18, { speed: 4.2, size: 0.16, color: K.clod });
    this.dust(at, 14, { size: 0.8, speed: 2.6, color: K.dust, life: 0.9 });
    this.smoke(at, 6, { size: 0.9, life: 1.8, rise: 1.0, spread: 0.8, color: [0.45, 0.36, 0.34] });
    this.sparks(at, 20, { color: B.rim, speed: 4.5, up: 3, gravity: 8, life: 0.7, size: 0.18, floor: at.y + 0.02 });
    this.light(_b.copy(at).setY(at.y + 0.6), B.rim, 3.2, 4, 0.9);
    this.later(0.35, () => {
      this.debris(at, 10, { speed: 3, size: 0.12, color: K.clod });
      this.dust(at, 10, { size: 0.6, speed: 2, color: K.dust });
      this.ring(at, { size: 2.2 * size, life: 0.45, color: B.rim, bright: 1.6 });
    });
    this.sparkles(at, 16, { color: [1, 0.6, 0.3], radius: 0.6, rise: 1.8, life: 1.0, size: 0.2 });
    this.prio = prev;
  }
  /**
   * Boss death: glowing cracks racing out under it, a burst, a gold column and a coin fountain (coins arc high and
   * land in a ring). 0.7 trauma. The body's own crack-dissolve belongs to the units view.
   */
  bossFall(pos, { size = 1.4 } = {}) {
    const prev = this.prio; this.prio = PRIO.hero;
    const at = pos.clone(), H = PALETTE.holy, B = PALETTE.blood;
    for (let k = 0; k < 3; k++) this.later(k * 0.07, () => this.decal(at, { type: 'crack', size: (2.2 + k * 0.9) * size, life: 4.5, hold: 2.5, color: k === 0 ? H.rim : B.rim, bright: 2.2 - k * 0.4 }));
    this.flash(_a.copy(at).setY(at.y + 0.4), 1.8 * size, H.core);
    this.ring(at, { size: 3.4 * size, life: 0.6, color: H.rim, bright: 2.4 });
    this.sparks(_a.copy(at).setY(at.y + 0.3), 28, { color: H.rim, speed: 6, up: 3, gravity: 9, life: 0.8, size: 0.18, floor: at.y + 0.02 });
    this.beam(at, _c.copy(at).setY(at.y + 3.6), { life: 0.7, width: 0.9, color: H.hex });
    for (let i = 0; i < 6; i++) this.spawn({ pos: _a.copy(at).setY(at.y + 0.2), frame: 'glow', life: 0.9 + i * 0.06, size0: 0.8, size1: 0.15, color0: H.rim, bright: 2.4, fadeIn: 0.04, fadeOut: 0.7, vel: _b.set(rnd(-0.1, 0.1), 1.6 + i * 0.2, rnd(-0.1, 0.1)) });
    this.sparkles(at, 26, { color: H.rim, radius: 0.7, rise: 2.2, life: 1.4, size: 0.2 });
    this.smoke(at, 5, { size: 0.7, life: 1.6, rise: 0.9, color: [0.8, 0.78, 0.75] });
    // the coin fountain: 18 coins in three bursts, thrown high and wide, bouncing on the ground
    for (let w = 0; w < 3; w++) this.later(w * 0.12, () => {
      for (let i = 0; i < 6; i++) {
        const a = rnd(0, 6.283), sp = rnd(1.0, 2.0);
        this.spawn({ pos: _c.copy(at).setY(at.y + 0.3), frame: 'coin', anim: this.anims.coinspin ? 'coinspin' : null, animRate: rnd(14, 20), vel: _b.set(Math.cos(a) * sp, rnd(4.2, 5.6), Math.sin(a) * sp), gravity: 10, drag: 0.1,
                     life: rnd(1.4, 1.8), size0: 0.2, size1: 0.2, additive: false, color0: [1, 1, 1], fadeIn: 0, fadeOut: 0.15, rotVel: rnd(-10, 10), floor: at.y + 0.08 });
      }
    });
    this.light(_b.copy(at).setY(at.y + 0.8), H.rim, 3.5, 4.5, 1.0);
    this.kick(0.7);
    this.prio = prev;
  }

  // ---------------------------------------------------------------- tier-up (Phase 2: "Tier-3 Ascension")
  // The client (views/towers.ts) runs the timeline: ascendCharge while the old tower squashes, ascendBurst on the
  // model swap, `crown` every ~0.1 s above a tier-3 tower for as long as it stands.
  /** Anticipation: motes converge on the tower's top and a small ring tightens at its foot. */
  ascendCharge(pos, top, color = PALETTE.holy.rim, seconds = 0.3) {
    const at = pos.clone(), t = top.clone();
    this.burst(14, () => { const off = _d.set(rnd(-1, 1), rnd(-0.6, 0.4), rnd(-1, 1)).normalize().multiplyScalar(rnd(0.6, 1.0)); return { pos: _c.copy(t).add(off), frame: 'spark', vel: _b.copy(off).multiplyScalar(-1 / seconds), life: seconds, size0: 0.14, size1: 0.05, aspect: 0.4, stretch: 0.15, color0: [1, 1, 1], color1: color, bright: 2.4, fadeIn: 0.2, fadeOut: 0.2 }; });
    this.glowPulse(t, { size: 0.9, life: seconds + 0.1, color });
    this.decal(at, { type: 'rune', size: 2.0, life: seconds + 1.2, hold: seconds + 0.4, fadeIn: seconds, spin: 1.4, color, bright: 1.6, rot: 0 });
  }
  /** The swap: a white flash, a 3-unit light pillar in the element colour, a spark ring, the tier strip, a camera punch. */
  ascendBurst(pos, height, color = PALETTE.holy.rim, tier = 3) {
    const prev = this.prio; this.prio = PRIO.hero;
    const at = pos.clone(), hex = ((Math.min(1, color[0]) * 255) << 16) | ((Math.min(1, color[1]) * 255) << 8) | (Math.min(1, color[2]) * 255);
    this.flash(_a.copy(at).setY(at.y + height * 0.5), 1.9, [1, 1, 1]);
    this.beam(at, _c.copy(at).setY(at.y + 3 + height), { life: 0.6, width: tier >= 3 ? 0.6 : 0.45, color: hex });
    for (let i = 0; i < 8; i++) this.spawn({ pos: _a.copy(at).setY(at.y + 0.2), frame: 'glow', life: 0.7 + i * 0.05, size0: 0.5, size1: 0.1, color0: color, bright: 2.4, fadeIn: 0.04, fadeOut: 0.6, vel: _b.set(0, 2.2 + i * 0.35, 0) });
    this.ring(at, { size: 2.4, life: 0.5, color, bright: 2.4 });
    this.sparks(_a.copy(at).setY(at.y + 0.15), 16, { color, speed: 3.5, up: 1.2, gravity: 6, life: 0.5, size: 0.13, floor: at.y + 0.02 });
    this.tierUp(_a.copy(at).setY(at.y + height * 0.55), 1.6, { color });
    this.light(_b.copy(at).setY(at.y + height * 0.6), color, 3, 3.5, 0.8);
    this.kick(tier >= 3 ? 0.3 : 0.18);
    this.prio = prev;
  }
  /**
   * The tier-up's celebration on top of `ascendBurst` (owner report 2026-09-23: "there needs to be more effects when a
   * tower is upgraded"), at the model swap: a shockwave rolling out from the foot with a dust skirt, a spiral fountain
   * of gold sparks climbing the tower for ~0.8 s, and gold halo rings sweeping up it (the HUD's floating "TIER n"
   * ribbon names the tier; a row of star sprites over the roof was tried and read as faint twinkles). ~40 pooled
   * sprites, hero priority.
   */
  towerLevelUp(pos, height, { color = PALETTE.holy.rim } = {}) {
    const L = LEVEL_UP, prev = this.prio; this.prio = PRIO.hero;
    const at = pos.clone(), K = PALETTE.kinetic;
    this.ring(at, { size: L.shockSize, life: L.shockLife, color: [1, 0.92, 0.7], flat: false, bright: 1.8 });
    this.decal(at, { type: 'dust', size: L.shockSize * 0.9, life: 0.8, color: K.dust, alpha: 0.8 });
    this.dust(at, 8, { size: 0.45, speed: 2.2, color: K.dust, life: 0.7 });
    for (let w = 0; w < L.fountainWaves; w++) this.later(w * L.fountainGap, () => {
      const pr = this.prio; this.prio = PRIO.hero;
      this.burst(L.fountainPerWave, (i) => {
        const a = (i / L.fountainPerWave) * 6.283 + w * 0.9, r = rnd(0.3, 0.45);
        return { pos: _c.set(at.x + Math.cos(a) * r, at.y + 0.1, at.z + Math.sin(a) * r), frame: i % 3 ? 'spark' : 'star',
          vel: _d.set(-Math.sin(a) * 1.1 - Math.cos(a) * 0.25, rnd(2.6, 3.6) + height * 0.6, Math.cos(a) * 1.1 - Math.sin(a) * 0.25),
          gravity: 2.2, drag: 0.6, life: rnd(0.6, 0.85), size0: 0.16, size1: 0.04, aspect: i % 3 ? 0.4 : 1, stretch: i % 3 ? 0.18 : 0,
          color0: [1, 1, 0.9], color1: color, bright: 2.4, fadeIn: 0.05, fadeOut: 0.4, rot: rnd(0, 6.3), rotVel: rnd(-4, 4) };
      });
      this.prio = pr;
    });
    // the level-up halo: gold rings laid flat, sweeping from the foot up past the roof, one after another
    const ring = this.anims.shock?.[L.ringFrame] ?? 'glow';
    for (let k = 0; k < L.rings; k++) this.later(k * L.ringGap, () => {
      const pr = this.prio; this.prio = PRIO.hero;
      this.spawn({ pos: _c.copy(at).setY(at.y + 0.1), frame: ring, flat: true, vel: _d.set(0, (height + L.ringOver) / L.ringLife, 0), life: L.ringLife, size0: L.ringSize[0], size1: L.ringSize[1], color0: color, color1: [1, 0.85, 0.4], bright: L.ringBright, fadeIn: 0.15, fadeOut: 0.35 });
      this.prio = pr;
    });
    this.prio = prev;
  }

  /** One beat of a tier-3 tower's floating element crown: three motes orbiting above its top (call ~10×/s). */
  crown(top, time, color = PALETTE.holy.rim, radius = 0.32) {
    const prev = this.prio; this.prio = PRIO.ambient;
    for (let k = 0; k < 3; k++) {
      const a = time * 2.4 + k * 2.094;
      this.spawn({ pos: _c.set(top.x + Math.cos(a) * radius, top.y + 0.05 * Math.sin(time * 3 + k), top.z + Math.sin(a) * radius), frame: 'glow', life: 0.45, size0: 0.14, size1: 0.04,
                   color0: [1, 1, 1], color1: color, bright: 2.2, fadeIn: 0.1, fadeOut: 0.6, vel: _b.set(-Math.sin(a) * 0.6, 0.12, Math.cos(a) * 0.6) });
    }
    this.prio = prev;
  }

  // ---------------------------------------------------------------- the Dragonlord (Phase 2: "The Dragonlord reveal")
  /** A fire gout from the dragon's jaw along `dir` (unit, world): 0.8 s of rolling flame, a scorch where it lands. */
  dragonBreath(jaw, dir, { length = 3.2, seconds = 0.8 } = {}) {
    const prev = this.prio; this.prio = PRIO.hero;
    const at = jaw.clone(), d = dir.clone().normalize(), F = PALETTE.fire;
    const n = Math.round(seconds / 0.04);
    for (let k = 0; k < n; k++) this.later(k * 0.04, () => {
      const sp = length / 0.55;
      this.spawn({ pos: at, anim: 'explosion', life: 0.55, size0: 0.25, size1: 1.1, sizeEase: ease.out, vel: _b.copy(d).multiplyScalar(sp * rnd(0.85, 1.1)).add(_d.set(rnd(-0.5, 0.5), rnd(-0.2, 0.4), rnd(-0.5, 0.5))),
                   drag: 1.2, fadeIn: 0, fadeOut: 0.45, color0: F.core, color1: F.rim, bright: 1.5, rot: rnd(0, 6.3), rotVel: rnd(-3, 3) });
      if (k % 3 === 0) this.embers(_a.copy(at).addScaledVector(d, rnd(0.5, length)), 2);
    });
    const land = at.clone().addScaledVector(d, length);
    this.later(0.3, () => this.decal(land, { type: 'scorch', size: 1.6, life: 4, hold: 2.4, color: [0.09, 0.07, 0.06], bright: 1.4 }));
    this.later(0.3, () => this.smoke(land, 4, { size: 0.7, life: 1.6, rise: 1, color: F.smoke }));
    this.light(at, F.rim, 3.2, 3.5, seconds + 0.3);
    this.kick(0.15);
    this.prio = prev;
  }

  /**
   * Wave start (vfx.md F11): the portal flares — a violet light and a swelling glow. It plays with the client's
   * WaveFx.stageStarted (render/waveFx.ts: the flat surge rings, sparkles and the one camera kick), so the two make a
   * single beat: the flare is ours, the rings and the kick are WaveFx's. The shock that used to run down the first road
   * cells and the upright ring were dropped (both beats played at once, the wave start read twice).
   */
  waveStart(portal) {
    const prev = this.prio; this.prio = PRIO.impact;
    const A = PALETTE.arcane, at = portal.clone();
    this.light(_b.copy(at).setY(at.y + 0.6), A.rim, 3, 3, 0.7);
    this.glowPulse(_a.copy(at).setY(at.y + 0.5), { size: 1.4, life: 0.45, color: A.rim });
    this.prio = prev;
  }

  // ---------------------------------------------------------------- tower life (towers.md F9)
  /** A short arc between two coils (lightning towers' idle). Dropped when the bolt pool is busy. */
  idleArc(a, b) { return this.lightning(a, b, { life: 0.08, width: 0.05, glow: 0.14, core: 0.018, coreBright: 2.5, jag: 0.6, ambient: true }); }
  /** One ember off a fire tower (ambient: the first thing dropped when the pool is full). */
  idleEmber(pos) { const prev = this.prio; this.prio = PRIO.ambient; this.embers(pos, 1, { radius: 0.06 }); this.prio = prev; }
  /** The kick-back puff at a heavy tower's feet on each shot (cannon, mortar). */
  shotDust(pos) { const prev = this.prio; this.prio = PRIO.muzzle; this.dust(pos, 6, { size: 0.28, speed: 1.1, color: PALETTE.kinetic.dust, life: 0.5 }); this.prio = prev; }

  // ---------------------------------------------------------------- element towers and status marks
  /**
   * The frost crystal gathering cold for `seconds` (the clip's release frame) before it looses a shard: motes drawn
   * in from a shell around it and a cold glow that swells until the release and goes out in `ELEMENT.frost.chargeTail`.
   * Anticipation on the crystal, kept small and additive-light so the bloom does not turn it into a flash.
   * `scale` grows with the tier.
   */
  frostCharge(core, seconds = 0.35, scale = 1) {
    const prev = this.prio; this.prio = PRIO.muzzle;
    const R = PALETTE.frost, F = ELEMENT.frost, c = _b.copy(core), life = Math.max(0.1, seconds);
    this.burst(Math.round(F.chargeMotes * scale), () => {
      const dir = _d.set(rnd(-1, 1), rnd(-0.3, 1), rnd(-1, 1)).normalize(), r = F.chargeRadius * scale, l = life * rnd(0.55, 1);
      return { pos: _c.copy(c).addScaledVector(dir, r), frame: 'star', vel: dir.multiplyScalar(-r / l), life: l, size0: 0.05, size1: 0.11 * scale, color0: R.mote, color1: R.rim, bright: 1.5, fadeIn: 0.4, fadeOut: 0.15, rot: rnd(0, 6.3), rotVel: rnd(-4, 4) };
    });
    const k = life / (life + F.chargeTail);
    this.spawn({ pos: c, frame: 'glow', life: life + F.chargeTail, size0: 0.06, size1: F.chargeGlow * scale, sizeEase: t => (t < k ? (t / k) * (t / k) : 1 - (t - k) / (1 - k)), color0: R.rim, color1: R.core, bright: 1.25, fadeIn: k, fadeOut: 1 - k });
    this.prio = prev;
  }

  /**
   * The beacon's aura on the ground, drawn THIS frame: a ring of `count` soft dashes laid along a circle of `radius`
   * around `center` (each dash overlaps the next, so it reads as one glowing line), every one set on the ground by
   * `heightAt(x, z)` so the ring follows the relief, turning at `spin` rad/s (`time` s). Every fourth dash carries a
   * brighter node of `size`, so the turn reads. Call it every frame for the standing ring, and with a growing radius
   * and falling `alpha` for the pulse. Stamps: no decal slot, no extra draw.
   */
  auraRing(center, radius, heightAt, { count = 40, time = 0, spin = 0.1, alpha = 0.5, size = 0.22, thickness = 0.1, color = PALETTE.holy.rim, lift = 0.04 } = {}) {
    const n = Math.max(3, count | 0), a0 = time * spin, dash = 6.283 * radius / n * 1.35;
    for (let i = 0; i < n; i++) {
      const a = a0 + i * 6.283 / n, ca = Math.cos(a), sa = Math.sin(a), x = center.x + ca * radius, z = center.z + sa * radius;
      _c.set(x, heightAt(x, z) + lift, z);
      // a flat quad's x runs along world x and its y along world −z: the tangent (−sin a, cos a) is this roll
      this._stamp(_c, 'glow', dash, color[0], color[1], color[2], alpha, true, Math.atan2(-ca, -sa), thickness / dash, true);
      if (i % 4 === 0) this._stamp(_c, 'glow', size, color[0], color[1], color[2], alpha, true, 0, 1, true);
    }
  }
  /** A tower inside the beacon's aura as the pulse passes it: gold motes rise off it and a soft glow blinks. */
  buffMark(base, height = 1, { color = PALETTE.holy.rim } = {}) {
    const prev = this.prio; this.prio = PRIO.ambient;
    this.spawn({ pos: _a.copy(base).setY(base.y + height * 0.45), frame: 'glow', life: 0.45, size0: 0.25, size1: 0.6, color0: color, bright: 0.9, fadeIn: 0.3, fadeOut: 0.6 });
    this.sparkles(_a.copy(base).setY(base.y + 0.1), 4, { color, radius: 0.28, rise: 0.9, life: 0.8, size: 0.1 });
    this.prio = prev;
  }

  /**
   * The fireball in flight, drawn THIS frame (stamps; the client calls it every frame in place of the GLB's flat
   * emissive shroud, FIRE): a red heat halo, a hot flame body trailing behind the flight and a white-hot core. `vel`
   * is the flight direction (any length), `scale` the round's size (1: a tier-1 round at full size), `seed` 0–1.
   */
  fireball(pos, vel, scale = 1, time = 0, seed = 0) {
    const F = FIRE, s = scale, len = vel.length();
    this._stamp(pos, 'glow', F.halo * s, F.haloColor[0], F.haloColor[1], F.haloColor[2], F.haloAlpha, true, 0, 1, false);
    if (len > 1e-6) {
      // the flame cell's tip (its top) laid along the tail: rot −90° puts the cell's y on the axis
      const tail = _f.copy(vel).multiplyScalar(-1 / len), w = F.body * s;
      _c.copy(pos).addScaledVector(tail, w * F.bodyLength * 0.3);
      this._stamp(_c, this.frameLoop('flame', time * F.flicker + seed * 8), w, 1, 1, 1, 1, true, -Math.PI / 2, F.bodyLength, false, this.hot(F.heat.body), tail);
    }
    const k = F.coreBright;
    this._stamp(pos, 'glow', F.core * s, F.coreColor[0] * k, F.coreColor[1] * k, F.coreColor[2] * k, 1, true, 0, 1, false);
  }

  /**
   * One brazier flame on a fire tower, drawn THIS frame (stamps, in place of the model's flat flame cone): a flame
   * tongue standing on `base`, FIRE.tongue × `height` (the cone's height at the pose the clips give it) tall, over a
   * warm glow in the bowl. `seed` (0–1) keeps neighbouring flames out of step.
   */
  towerFlame(base, height, time = 0, seed = 0) {
    const F = FIRE, h = height * F.tongue;
    if (!(h > 1e-4)) return;
    _c.copy(base); _c.y += h * 0.42;                       // the cell's flame stands on its bottom tenth
    this._stamp(_c, this.frameLoop('flame', time * F.flicker + seed * 8), h * 0.5, 1, 1, 1, 1, true, 0, 2, false, this.hot(F.heat.brazier));
    _c.copy(base); _c.y += height * 0.3;
    this._stamp(_c, 'glow', height * F.bowl, F.bowlColor[0], F.bowlColor[1], F.bowlColor[2], F.bowlAlpha, true, 0, 1, false);
  }
  /** A wisp of soot off a fire tower's brazier (ambient: dropped first when the pool is full). */
  idleSoot(pos) { const prev = this.prio; this.prio = PRIO.ambient; this.fireSmoke(pos, 1, { size: 0.22, life: 1.6, rise: 0.5, spread: 0.12 }); this.prio = prev; }

  /**
   * Status marks for one afflicted enemy, drawn THIS frame only (call every frame while the status lasts): `feet` is
   * the ground point under it, `h` its height. `st`: `frost` 0–1 (slow strength, fading out as it wears off),
   * `frozen` (held still), `burn` stacks, `shred` stacks, `seed` (per unit, 0–1) and `time` (s). See STATUS.
   */
  enemyStatus(feet, h, st, dt = 1 / 60) {
    const prev = this.prio; this.prio = PRIO.ambient;
    const S = STATUS, seed = st.seed || 0, time = st.time || 0;
    if (st.frost > 0.01) {                                  // frost crust: crystals out of the body, a cold disc below
      const R = PALETTE.frost, f = Math.min(1, st.frost), n = st.frozen ? S.frozenCrystals : S.crystals;
      const size = h * S.crystalSize * (st.frozen ? 1.25 : 1) * (0.55 + 0.45 * f);
      for (let i = 0; i < n; i++) {
        const a = seed * 6.283 + i * 6.283 / n, r = h * S.crystalRing, up = S.crystalLow + (S.crystalHigh - S.crystalLow) * ((seed + i * 0.618) % 1);
        _c.set(feet.x + Math.cos(a) * r, feet.y + h * up, feet.z + Math.sin(a) * r);
        this._stamp(_c, ICE[(i + ((seed * 7) | 0)) % 3], size, 1, 1, 1, f, false, Math.cos(a) * 0.5, 1, false);
      }
      // the glaze: a pale, normal-blended haze over the body, so the unit reads iced over, not just tinted
      _c.set(feet.x, feet.y + h * 0.45, feet.z);
      this._stamp(_c, 'glow', h * S.glaze, R.mote[0], R.mote[1], R.mote[2], f * (st.frozen ? S.frozenGlazeAlpha : S.glazeAlpha), false, 0, 1.2, false);
      _c.set(feet.x, feet.y + 0.02, feet.z);
      this._stamp(_c, 'glow', h * S.frostDisc, R.rim[0], R.rim[1], R.rim[2], f * S.frostDiscAlpha, true, 0, 1, true);
      if (Math.random() < dt * S.frostMoteRate * f) this.sparkles(_c.set(feet.x, feet.y + h * 0.5, feet.z), 1, { color: R.mote, radius: h * 0.35, rise: 0.25, life: 0.5, size: 0.08 });
    }
    if (st.burn > 0) {                                      // one flame tongue per burn stack
      const n = Math.min(3, st.burn), size = h * S.flameSize;
      for (let i = 0; i < n; i++) {
        const a = seed * 6.283 + i * 2.094 + 0.8, r = h * S.flameRing;
        _c.set(feet.x + Math.cos(a) * r, feet.y + size * 0.5 + h * 0.12 * i, feet.z + Math.sin(a) * r);
        // kept below white: the hot ramp's core, summed under bloom, would wash the unit out
        this._stamp(_c, this.frameLoop('flame', time * 14 + i * 3 + seed * 8), size * 0.5, S.burnBright, S.burnBright, S.burnBright, 0.9, true, 0, 2, false, this.hot(FIRE.heat.burn));
      }
      if (Math.random() < dt * S.emberRate * n) this.embers(_c.set(feet.x, feet.y + h * 0.5, feet.z), 1, { radius: h * 0.25 });
    }
    if (st.shred > 0) {                                     // poison: a green pip per shred stack, circling the chest
      const Pn = PALETTE.poison, n = Math.min(5, st.shred), r = h * S.pipRing;
      for (let i = 0; i < n; i++) {
        const a = time * S.pipSpin + i * 6.283 / n + seed * 6.283;
        _c.set(feet.x + Math.cos(a) * r, feet.y + h * S.pipHeight + Math.sin(time * 3 + i) * h * 0.04, feet.z + Math.sin(a) * r);
        this._stamp(_c, 'glow', h * S.pipSize, Pn.rim[0] * 1.8, Pn.rim[1] * 1.8, Pn.rim[2] * 1.8, 1, true, 0, 1, false);
      }
      if (Math.random() < dt * S.bubbleRate * (0.5 + n * 0.25)) this.bubbles(_c.set(feet.x, feet.y + h * 0.3, feet.z), 1, { color: Pn.core, radius: h * 0.3, size: 0.07 });
    }
    this.prio = prev;
  }

  /**
   * A sprite for the NEXT `update` only (immediate mode): marks that ride a moving unit are re-stamped every frame at
   * its position rather than spawned as particles that would drift off it. Pooled, positional arguments (called
   * hundreds of times a frame: no option objects), at most `STATUS.maxStamps` a frame; drawn in the particle layers.
   * `ramp` names a colour ramp for a data frame, or is a ramp index (`hot(heat)`; the frame's own when null); `axis` (a world direction, or null for
   * camera-facing) lays the quad's x along it, as a stretched particle's velocity does.
   */
  _stamp(pos, frame, size, r, g, b, alpha, additive, rot, aspect, flat, ramp = null, axis = null) {
    if (this.stamps.length >= STATUS.maxStamps) return;
    const s = this.stampFree.pop() || { pos: V3(), axis: V3() };
    s.pos.copy(pos); s.frame = frame; s.size = size; s.r = r; s.g = g; s.b = b; s.alpha = alpha; s.additive = additive; s.rot = rot; s.aspect = aspect; s.flat = flat;
    s.ramp = typeof ramp === 'number' ? ramp : ramp != null ? (this.rampIndex[ramp] ?? null) : null;
    if (axis) s.axis.copy(axis); else s.axis.set(0, flat ? 2 : 0, 0);
    this.stamps.push(s);
  }
  _drawStamps() {
    const T = this.stamps;
    for (let i = 0; i < T.length; i++) {
      const s = T[i], uv = this.uv[s.frame] || this.uv.glow, fr = this.frameRamp[s.frame];
      const ramp = fr === undefined ? -1 : fr >= 0 && s.ramp !== null ? s.ramp : fr;
      (s.additive ? this.add : this.norm).push(s, s.pos.x, s.pos.y, s.pos.z, s.size, s.size * s.aspect, s.rot, s.r, s.g, s.b, s.alpha, uv, s.axis.x, s.axis.y, s.axis.z, ramp);
      this.stampFree.push(s);
    }
    T.length = 0;
  }

  // One call per tower id — muzzle + (for hitscan towers) the whole shot.
  towerFire(id, muzzle, target) {
    if (id === 'lightning') { this.teslaFire(muzzle, [target]); return; }
    if (id === 'beacon') { this.muzzle('beacon', muzzle, target); this.beam(muzzle, target, { life: 0.25, width: 0.22, color: PALETTE.holy.hex }); this.impact('beacon', target); return; }
    this.muzzle(id, muzzle, target);
  }

  // One call per unit id, mirroring towerFire. Melee units have no projectile: this returns false so the caller
  // knows to fall back to meleeHit when the sim's swing lands.
  unitFire(id, muzzle, target) {
    const kind = VFX.UNIT_PROJECTILE[id];
    if (!kind) return false;
    this.muzzle(kind, muzzle, target);
    return true;
  }
  // ---------------------------------------------------------------- reward chest
  // The end-of-level chest is the climax, so it gets its own three-call sequence that lines up with the baked
  // clips. `rank` is 0 (common) .. 5 (mythic); everything scales with it.
  //   chestShake(pos, rank)              every ~0.6 s while the `shake` clip loops and the player is deciding
  //   chestBurst(pos, rank, colour)      on frame 32 of `open` -- the lock snapping and the lid being thrown
  //   chestAura(pos, rank, colour)       every ~0.4 s afterwards while the loot hangs there
  chestShake(pos, rank = 0) {
    const at = pos.clone();
    const k = 1 + rank * 0.3;
    this.dust(at, 1 + Math.round(rank / 2), { size: 0.18 * k, speed: 0.7 * k, life: 0.4 });
    if (rank >= 2) this.sparks(at.clone().add(V3(0, 0.16, 0)), 1 + rank, { color: [1, 0.9, 0.55], speed: 0.9 * k, up: 1.0, gravity: 3, life: 0.4, size: 0.06 });
    if (rank >= 4) this.glowPulse(at.clone().add(V3(0, 0.14, 0)), { size: 0.5 * k, life: 0.3, color: [1, 0.92, 0.6] });
    this.shake = Math.max(this.shake, 0.05 * k);
  }

  chestBurst(pos, rank = 0, color = [1, 0.9, 0.55]) {
    const at = pos.clone();
    const up = at.clone().add(V3(0, 0.22, 0));
    const k = 1 + rank * 0.34;
    // a rank-coloured flash, not a white one: at 0.9·k white it blanked the whole stage for three frames
    this.flash(up, 0.42 * k, [0.45 + 0.55 * color[0], 0.45 + 0.55 * color[1], 0.45 + 0.55 * color[2]]);
    this.ring(at, { size: 1.5 * k, life: 0.45, color });
    this.later(0.06, () => this.ring(at, { size: 2.0 * k, life: 0.6, color: [0.6 + 0.4 * color[0], 0.6 + 0.4 * color[1], 0.6 + 0.4 * color[2]] }));
    // the column of light out of the lid (the chest stage's pillar, render/chestShow.ts, carries on from it)
    for (let i = 0; i < 3 + rank; i++) {
      this.spawn({ pos: up.clone(), frame: 'glow', life: 0.9 + i * 0.05, size0: 0.32 * k, size1: 0.08,
                   color0: color, bright: 1.5, fadeIn: 0.04, fadeOut: 0.7, vel: V3(rnd(-0.1, 0.1), 1.4 + i * 0.15, rnd(-0.1, 0.1)) });
    }
    // coins and sparks thrown out of the mouth
    this.burst(14 + rank * 5, () => {
      const a = rnd(0, 6.283), sp = rnd(1.4, 3.2) * k;
      return { pos: up.clone(), frame: 'star', vel: V3(Math.cos(a) * sp * 0.5, rnd(2.2, 3.8) * k, Math.sin(a) * sp * 0.5),
               gravity: 5.5, life: rnd(0.9, 1.5), size0: 0.10, size1: 0.20, color0: [1, 0.85, 0.35], bright: 2.2,
               fadeIn: 0.05, fadeOut: 0.35, rot: a, rotVel: rnd(-8, 8) };
    });
    this.sparkles(up, 14 + rank * 4, { color, radius: 0.5 * k, rise: 2.0, life: 1.3, size: 0.16 });
    this.smoke(at, 2, { size: 0.25 * k, life: 0.8, rise: 0.7, spread: 0.5 });
    if (rank >= 3) this.later(0.10, () => this.beam(at, at.clone().add(V3(0, 3.2, 0)), { life: 0.5, width: 0.4 * k, color: new THREE.Color(0.6 + 0.4 * color[0], 0.6 + 0.4 * color[1], 0.6 + 0.4 * color[2]).getHex() }));
    if (rank >= 5) for (let i = 0; i < 3; i++) this.later(0.08 * i, () => this.ring(at, { size: 1.4 + i * 0.6, life: 0.5, color: [1, 0.55, 0.45], bright: 1.6 }));
    this.shake = Math.max(this.shake, 0.5 + 0.1 * rank);
  }

  chestAura(pos, rank = 0, color = [1, 0.9, 0.55]) {
    const up = pos.clone().add(V3(0, 0.30, 0));
    this.sparkles(up, 2 + rank, { color, radius: 0.34, rise: 0.9, life: 1.2, size: 0.10 });
    if (rank >= 4) this.glowPulse(up, { size: 0.5, life: 0.5, color });
  }


  /**
   * A tower lands (vfx.md F11). The client drops the model in over 120 ms (views/towers.ts), so the landing — dust
   * skirt on the ground, clods, a warm light, 0.12 trauma — waits those 120 ms.
   */
  towerPlace(pos) {
    const at = pos.clone(), K = PALETTE.kinetic;
    this.later(0.12, () => {
      this.decal(at, { type: 'dust', size: 2.2, life: 0.7, color: K.dust, alpha: 0.9 });
      this.ring(at, { size: 1.8, life: 0.5, color: [1, 0.85, 0.4], bright: 1.6 });
      this.dust(at, 10, { size: 0.5, speed: 1.8, color: K.dust });
      this.debris(at, 5, { speed: 2.2, size: 0.08, color: K.clod });
      this.sparkles(at, 12, { radius: 0.4, rise: 1.6, life: 1.0, size: 0.2 });
      this.flash(_a.copy(at).setY(at.y + 0.4), 1.0, [1, 0.9, 0.6]);
      this.light(_b.copy(at).setY(at.y + 0.6), [1, 0.85, 0.6], 2.2, 1.2, 1.5);
      this.kick(0.12);
    });
  }
  /**
   * Tier-up celebration (art/interface/vfx/tierup_strip, 8 frames additive) at the tower's turret height; `scale` in
   * cells. Phase 2 ("Tier-3 Ascension") passes the element `color` for the light pillar and adds the anticipation.
   */
  tierUp(pos, scale = 1.6, { color = [1, 0.85, 0.35] } = {}) {
    if (this.anims.tierup) this.spawn({ pos, frame: 'tierup_00', anim: 'tierup', life: 8 / 24, size0: scale, size1: scale * 1.15, color0: [1, 0.95, 0.75], bright: 1.6, fadeIn: 0, fadeOut: 0.12 });
    this.burst(14, () => ({ pos, frame: 'star', vel: _d.set(rnd(-1.2, 1.2), rnd(0.6, 2.2), rnd(-1.2, 1.2)), gravity: -2.5, life: rnd(0.6, 1.0), size0: 0.04, size1: 0.16, color0: color, bright: 2, fadeOut: 0.3, rot: rnd(0, 6.3), rotVel: 4 }));
  }

  towerUpgrade(pos) {
    const at = pos.clone();
    this.ring(at, { size: 1.4, life: 0.5, color: [1, 0.85, 0.4] });
    this.burst(18, () => ({ pos: _c.copy(at).add(_d.set(rnd(-0.35, 0.35), 0, rnd(-0.35, 0.35))), frame: 'star', vel: _b.set(0, rnd(1.5, 2.6), 0), life: rnd(0.7, 1.1), size0: 0.05, size1: 0.22, sizeEase: t => Math.sin(t * Math.PI), color0: [1, 0.85, 0.35], bright: 2.2, fadeIn: 0.1, fadeOut: 0.3, rot: rnd(0, 6.3), rotVel: 3 }));
    this.light(_b.copy(at).setY(at.y + 0.9), PALETTE.holy.rim, 2, 1.5, 0.6);
    this.later(0.25, () => this.flash(_a.copy(at).setY(at.y + 0.9), 1.0, [1, 0.9, 0.6]));
  }
}

// Which projectile each shooter fires, and the vfx kind that dresses it. `null` = melee, no projectile.
// Keep this in step with art/hq/README.md "Projectiles"; the model name is what the client spawns from the library.
VFX.TOWER_PROJECTILE = {
  archer: 'proj_arrow', ballista: 'proj_bolt', cannon: 'proj_cannonball', mortar: 'proj_shell',
  frost: 'proj_shard', fire: 'proj_fireball', poison: 'proj_glob',
  lightning: null, beacon: null,                      // hitscan - use towerFire()
  barracks: null, arcane: null, hex: null,            // soldiers, a held beam, a curse aura (packages/web towerV2 views)
};
VFX.UNIT_PROJECTILE = {
  enemy_bomber: 'bomb', enemy_healer: 'heal', boss_necromancer: 'soul', boss_ironclad: 'slug',
  boss_plague_lord: 'plague',
  enemy_raider: null, enemy_runner: null, enemy_brute: null, enemy_shieldbearer: null, enemy_flyer: null,
  boss_warlord: null, boss_colossus: null, boss_harpy_queen: null, troop_soldier: null,
};
VFX.UNIT_PROJECTILE_MODEL = {
  bomb: 'proj_bomb', heal: 'proj_heal', soul: 'proj_soul', slug: 'proj_slug', plague: 'proj_glob',
  unit_bow: 'proj_arrow_s',
};
// Each tower's element colour (tier-up pillar, crown): the PALETTE rims, gold for the kinetic towers.
VFX.TOWER_COLOR = {
  archer: [1, 0.86, 0.45], ballista: [0.9, 0.92, 1], cannon: [1, 0.66, 0.3], mortar: [1, 0.66, 0.3],
  frost: PALETTE.frost.rim, fire: PALETTE.fire.rim, poison: PALETTE.poison.rim, lightning: PALETTE.arcane.rim, beacon: PALETTE.holy.rim,
  // TOWERS_V2: the Barracks' banner gold, the Arcane Spire's rose-magenta crystal (#FF4FC8), the Hex Totem's violet (#B45CFF)
  barracks: [1, 0.78, 0.38], arcane: [1, 0.31, 0.78], hex: [0.71, 0.36, 1],
};
// World units a projectile travels between two `trail()` calls (vfx.md F4): smoke puffs are larger, so sparser.
VFX.TRAIL_SPACING = {
  archer: 0.1, ballista: 0.1, cannon: 0.12, mortar: 0.12, bomb: 0.12, fire: 0.08, poison: 0.08, plague: 0.08,
  frost: 0.11, beacon: 0.1, heal: 0.08, soul: 0.08, slug: 0.1, unit_bow: 0.08,
};

// Rarity -> the accent colour to pass to chestBurst / chestAura, matching each chest model's gem.
VFX.CHEST_COLOR = {
  common:    [0.95, 0.84, 0.62],
  uncommon:  [0.55, 1.00, 0.35],
  rare:      [0.55, 0.80, 1.00],
  epic:      [0.72, 0.40, 1.00],
  legendary: [1.00, 0.72, 0.25],
  mythic:    [1.00, 0.36, 0.40],
};
VFX.CHEST_RANK = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4, mythic: 5 };
